import React, { ChangeEvent, FormEvent, useEffect, useRef, useState } from 'react';
import logo from './Logo-Big.jpg';
import './App.css';
import { Accordion, Alert, Button, Col, Container, Form, Modal, Row } from 'react-bootstrap';
import { FaPlus } from '@react-icons/all-files/fa/FaPlus';
import { FaAsterisk } from '@react-icons/all-files/fa/FaAsterisk';
import AddWohnsitzComponent, { Wohnsitz } from './AddWohnsitzComponent/AddWohnsitzComponent';
import WohnsitzZeile from './WohnsitzZeileComponent/WohnsitzZeile';
import AddArbeitComponent, { Arbeit } from './AddArbeitComponent/AddArbeitComponent';
import ArbeitsZeile from './ArbeitsZeileComponent/ArbeitsZeile';
import moment from 'moment';
import JSZip from 'jszip';
import { FaTimesCircle } from '@react-icons/all-files/fa/FaTimesCircle';
import { Constants } from './constants';
import { StatesAlpha2List } from './statesAlpha2List';
import StrafregisterInput from './StrafregisterInput/StrafregisterInput';
import { flughafenPdfs, flughafenSenden } from './portal/ApiClient';
import SignaturePad from './portal/SignaturePad';
import { komprimiereBild } from './bildKompressor';
import { findeLuecken, hatOffenenEintrag } from './luecken';

import { PdfFormData } from './pdfGenerator/pdfTypes';
import { generateDiff, GESCHLECHT_LABEL } from './generateDiff';
import { trimTiefe } from './trimTiefe';
import ModeSelection from './ModeSelection/ModeSelection';
import { AppMode } from './types';
import { useKeycloak } from '@react-keycloak/web';
import MeinZuep from './portal/MeinZuep';
import BgWechsel from './BgWechsel/BgWechsel';
import StrafregisterIt from './portal/StrafregisterIt';
import DokumentNachreichen from './portal/DokumentNachreichen';
import MatchingReview from './admin/MatchingReview';
import AntraegeAdmin from './admin/AntraegeAdmin';
import { ApiError, getMe, submitAntrag, getViewAs } from './portal/ApiClient';

/** Live-Lückenanzeige unter den Wohnsitz-/Beschäftigungslisten: prüft bei
 *  jeder Eingabe sofort auf Lücken > 28 Tage im 5-Jahres-Fenster — dieselbe
 *  Regel, mit der die Behörde später abweist (ws_luecke/bv_luecke). */
function LueckenAnzeige({ eintraege, was }: { eintraege: { von: Date | string; bis?: Date | string | null }[]; was: string }) {
  if (eintraege.length === 0) return null;
  const befund = findeLuecken(eintraege);
  const fmt = (d: Date) => moment(d).format('DD.MM.YYYY');
  if (befund.luecken.length === 0 && !befund.fehltAktuell) {
    return <Alert variant='success' className='py-2 mt-2 mb-0'>✅ {was} lückenlos für die letzten 5 Jahre.</Alert>;
  }
  return (
    <Alert variant='danger' className='py-2 mt-2 mb-0'>
      <strong>⚠️ Achtung — die Behörde verlangt {was} lückenlos für die letzten 5 Jahre:</strong>
      <ul className='mb-0 mt-1'>
        {befund.luecken.map((l, i) => (
          <li key={i}>Lücke von {fmt(l.von)} bis {fmt(l.bis)} ({l.tage} Tage)</li>
        ))}
        {befund.fehltAktuell && <li>Es fehlt ein aktueller Eintrag ohne Enddatum („bis" leer lassen)</li>}
      </ul>
    </Alert>
  );
}

/** Flughafen-PDFs kommen IMMER tagesaktuell vom Signatur-Dienst (befüllt +
 *  von Dr. Claudia Moser signiert). Bewusst KEIN Client-Fallback auf alte
 *  Vorlagen -- lieber eine klare Fehlermeldung als ein veraltetes Formular. */
async function erzeugeFlughafenPdfs(
  token: string | undefined,
  pdfData: PdfFormData,
): Promise<[Uint8Array, Uint8Array]> {
  try {
    const r = await flughafenPdfs(token, pdfData as unknown as Record<string, string>);
    const dec = (b64: string) => Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
    return [dec(r.antrag), dec(r.anlage4b)];
  } catch (err) {
    throw new Error(
      'Die signierten Flughafen-Formulare konnten gerade nicht erzeugt werden — '
      + 'bitte versuche es in ein paar Minuten erneut. (' + (err as Error).message + ')',
    );
  }
}


/** ICAO-9303-Prüfziffer (Gewichte 7,3,1; A=10..Z=35) — erkennt, wenn jemand
 * die Dokumentnummer aus der MRZ (Rückseite, MIT angehängter Prüfziffer)
 * statt von der Vorderseite eingibt. */
function icaoCheckDigit(s: string): number {
  const weights = [7, 3, 1];
  let total = 0;
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    const v = ch >= '0' && ch <= '9' ? ch.charCodeAt(0) - 48 : ch.charCodeAt(0) - 55;
    total += v * weights[i % 3];
  }
  return total % 10;
}

/** Replace umlauts and special chars in filenames to avoid issues with the ZÜP portal */
function sanitizeFilename(name: string): string {
  return name
    .replace(/ä/g, 'ae').replace(/Ä/g, 'Ae')
    .replace(/ö/g, 'oe').replace(/Ö/g, 'Oe')
    .replace(/ü/g, 'ue').replace(/Ü/g, 'Ue')
    .replace(/ß/g, 'ss')
    .replace(/[^\w.-]/g, '_');
}

function App() {

  const { keycloak } = useKeycloak();

  // Browser-History-Anbindung: jeder Modus liegt als #hash in der URL --
  // damit funktionieren Browser-Zurück/-Vor und Reload innerhalb der App.
  const ALL_MODES: AppMode[] = ['zuep-verlaengerung', 'neuantrag', 'ausweis-verlaengerung', 'bg-wechsel', 'mein-zuep', 'matching-review', 'antraege-admin', 'strafregister-it', 'dokument-nachreichen'];
  const modeFromHash = (): AppMode | null => {
    const h = window.location.hash.replace('#', '');
    return (ALL_MODES as string[]).includes(h) ? (h as AppMode) : null;
  };
  // Deep-Links aus den Erinnerungs-Mails: ?kachel=<modus> überlebt den
  // Keycloak-Login-Redirect (der #hash tut das nicht zuverlässig) und
  // springt nach der Anmeldung direkt in die richtige Kachel. Bewusst nur
  // Mitglieds-Kacheln — Admin-Modi sind über Mail-Links nicht erreichbar.
  const MAIL_MODES = ['zuep-verlaengerung', 'neuantrag', 'ausweis-verlaengerung', 'mein-zuep', 'strafregister-it', 'dokument-nachreichen'];
  const modeFromQuery = (): AppMode | null => {
    const q = new URLSearchParams(window.location.search).get('kachel');
    return q && MAIL_MODES.includes(q) ? (q as AppMode) : null;
  };
  const [appMode, setAppMode] = useState<AppMode | null>(() => modeFromHash() ?? modeFromQuery());
  useEffect(() => {
    // Query-Parameter in den #hash überführen, damit Reload/Zurück wie bei
    // einer normal geöffneten Kachel funktionieren.
    const q = modeFromQuery();
    if (q && !modeFromHash()) {
      window.history.replaceState(null, '', window.location.pathname + '#' + q);
    }
    const onPop = () => setAppMode(modeFromHash());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const goBack = () => {
    if (window.history.state && (window.history.state as any).zuep) {
      window.history.back();
    } else {
      window.history.replaceState(null, '', window.location.pathname);
      setAppMode(null);
    }
  };
  // Formular wurde automatisch aus /api/me vorbefüllt -> Identitätskern-
  // Felder sind gesperrt (Quelle: ausweisgeprüfte ZÜP-Datenbank).
  const [apiPrefilled, setApiPrefilled] = useState(false);
  // Verbindliche Portal-Einreichung (Antrags-ZIP -> Gateway-Queue).
  const [antragBlob, setAntragBlob] = useState<Blob | null>(null);
  const [einreichRichtig, setEinreichRichtig] = useState(false);
  const [einreichKosten, setEinreichKosten] = useState(false);
  const [einreichBusy, setEinreichBusy] = useState(false);
  const [einreichErgebnis, setEinreichErgebnis] = useState<{ ok: boolean; text: string } | null>(null);

  const showZuepSections = appMode === 'zuep-verlaengerung' || appMode === 'neuantrag';
  const showPdfSection = appMode === 'neuantrag' || appMode === 'ausweis-verlaengerung';

  // Admin-„Ansehen als"-Modus ist server-seitig NUR lesend: jede Mutation
  // (Einreichen) läuft unter der eigenen Admin-Identität, nicht unter dem
  // betrachteten Mitglied -> der Antrag würde dem Admin zugeordnet. Darum
  // hier hart sperren, statt einen fehlzugeordneten Antrag zu erzeugen.
  const viewAs = getViewAs();

  const FULL_DATE_FORMAT = 'DD.MM.yyyy';
  const MONTH_DATE_FORMAT = 'MM.yyyy';
  const JSON_DATE_FORMAT = 'YYYY-MM-DD';
  const VALID_MIME_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'];
  const normalizeMime = (raw: string): string | null => {
    if (!raw) return null;
    const lower = raw.toLowerCase();
    return VALID_MIME_TYPES.find(m => m === lower) ?? null;
  };
  const groupClass = 'mb-3';
  const rowClass = 'py-2';
  const [validated, setValidated] = useState(false);
  const [docFormValidated, setDocFormValidated] = useState(false);

  const [showWohnsitzAdd, setShowWohnsitzAdd] = useState(false);
  const [wohnsitze, setWohnsitze] = useState<Array<Wohnsitz>>([]);

  const [showArbeitAdd, setShowArbeitAdd] = useState(false);
  const [arbeiten, setArbeiten] = useState<Array<Arbeit>>([]);

  const [showDialog, setShowDialog] = useState(false);

  const [geschlechtInvalidMessage, setGeschlechtInvalidMessage] = useState('');
  const [docArtInvalidMessage, setDocArtInvalidMessage] = useState('');
  const [selectedDocArt, setSelectedDocArt] = useState('-1');
  const [fileInvalidMessage, setFileInvalidMessage] = useState('');
  const [file2InvalidMessage, setFile2InvalidMessage] = useState('');
  const [fileKomprimiertInfo, setFileKomprimiertInfo] = useState('');
  const [file2KomprimiertInfo, setFile2KomprimiertInfo] = useState('');
  const [formErrorMessages, setFormErrorMessages] = useState<Array<string>>([]);

  const [wohnsitzeHasErrors, setWohnsitzeHasErrors] = useState(false);
  const [arbeitenHasErrors, setArbeitenHasErrors] = useState(false);

  const [staat1Invalidity, setStaat1Invalidity] = useState('');
  const [docNummerPattern, setDocNummerPattern] = useState('');
  const [docNummerHint, setDocNummerHint] = useState('');
  const [docNummerInvalidMessage, setDocNummerInvalidMessage] = useState('');

  const [json, setDataObject] = useState<Object>();
  const [jsonUrl, setJsonUrl] = useState('');
  const [state1, setState1] = useState<string>();
  // Die geladenen Dokumentdateien liegen nur im Ref (werden beim Absenden
  // gelesen, lösen kein Re-Render aus) -- kein State nötig.
  const loadedFilesRef = useRef<Map<string, File>>(new Map());
  const [loadedDocName, setLoadedDocName] = useState('');
  // Bereits in USP hinterlegtes, noch gültiges Ausweisdokument: wird ANGEZEIGT
  // statt sofort einen Upload zu verlangen; Upload nur auf Wunsch (Ersetzen).
  const [bestandsDok, setBestandsDok] = useState<{ art: number; nummer: string; ausstellung: string; gueltigBis: string; abgelaufen: boolean } | null>(null);
  const [dokErsetzen, setDokErsetzen] = useState(false);
  const [loadedDocMime, setLoadedDocMime] = useState('');
  const [loadedDoc2Name, setLoadedDoc2Name] = useState('');
  const [loadedDoc2Mime, setLoadedDoc2Mime] = useState('');
  const [docExpiredWarning, setDocExpiredWarning] = useState('');
  const [loadedStrafregister, setLoadedStrafregister] = useState<Array<any>>([]);
  const [loadedOriginalData, setLoadedOriginalData] = useState<any>(null);


  const [rechnungFirma, setRechnungFirma] = useState('');
  const [rechnungStrasse, setRechnungStrasse] = useState('');
  const [rechnungPlzort, setRechnungPlzort] = useState('');

  const [ausweisVorname, setAusweisVorname] = useState('');
  // Browser-Unterschrift + Direktversand an die Ausweisstelle
  const [unterschriftPng, setUnterschriftPng] = useState<string | null>(null);
  const [letzteAusweisDaten, setLetzteAusweisDaten] = useState<Record<string, string> | null>(null);
  const [ausweisSendet, setAusweisSendet] = useState(false);
  const [ausweisSendeErgebnis, setAusweisSendeErgebnis] = useState<string | null>(null);
  const [ausweisSendeFehler, setAusweisSendeFehler] = useState<string | null>(null);
  const [ausweisFamname, setAusweisFamname] = useState('');
  const [ausweisGebdatum, setAusweisGebdatum] = useState('');
  const [ausweisGebOrt, setAusweisGebOrt] = useState('');
  const [ausweisGebOrtPlz, setAusweisGebOrtPlz] = useState('');
  const [ausweisGebOrtLand, setAusweisGebOrtLand] = useState('');
  const [ausweisGeschlecht, setAusweisGeschlecht] = useState('-1');
  const [ausweisNationalitaet, setAusweisNationalitaet] = useState('');
  const [ausweisTel, setAusweisTel] = useState('');
  const [ausweisMail, setAusweisMail] = useState('');
  const [ausweisPlz, setAusweisPlz] = useState('');
  const [ausweisOrt, setAusweisOrt] = useState('');
  const [ausweisStrasse, setAusweisStrasse] = useState('');
  const [ausweisTaetigkeit, setAusweisTaetigkeit] = useState('Pilot');

  const requiredIcon = <FaAsterisk style={{ color: 'red', paddingLeft: '2px' }} ></FaAsterisk>

  const handleSubmitAusweisOnly = async () => {
    // Ausweis-Verlängerung: Validate form fields then generate ANTRAG & ANLAGE 4B PDFs
    const errors: string[] = [];
    if (!ausweisVorname.trim()) errors.push('Vorname fehlt');
    if (!ausweisFamname.trim()) errors.push('Familienname fehlt');
    if (!ausweisGebdatum) errors.push('Geburtsdatum fehlt');
    if (ausweisGeschlecht === '-1') errors.push('Geschlecht nicht ausgewählt');
    if (!ausweisNationalitaet) errors.push('Staatsangehörigkeit nicht ausgewählt');
    if (!ausweisMail.trim()) errors.push('E-Mail fehlt');

    if (errors.length > 0) {
      setFormErrorMessages(errors);
      setTimeout(() => errorSummaryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
      return;
    }

    try {
      const geschlechtLabel: Record<string, string> = { '0': 'männlich', '1': 'weiblich', '2': 'divers' };
      const pdfData: PdfFormData = {
        vorname: ausweisVorname.trim(),
        famname: ausweisFamname.trim(),
        nationalitaet: StatesAlpha2List.ALPHA2CODES[ausweisNationalitaet] || ausweisNationalitaet,
        gebdatum: ausweisGebdatum ? moment(ausweisGebdatum).format(FULL_DATE_FORMAT) : '',
        geb_ort_plz: ausweisGebOrtPlz,
        geb_ort: ausweisGebOrt,
        geb_ort_land: StatesAlpha2List.ALPHA2CODES[ausweisGebOrtLand] || ausweisGebOrtLand,
        geschlecht: geschlechtLabel[ausweisGeschlecht] || '',
        kontakt_tel: ausweisTel,
        kontakt_mail: ausweisMail.trim(),
        adresse_plz: ausweisPlz,
        adresse_ort: ausweisOrt,
        adresse_strasse: ausweisStrasse,
        rechnung_firma: rechnungFirma,
        rechnung_strasse: rechnungStrasse,
        rechnung_plzort: rechnungPlzort,
        taetigkeit: ausweisTaetigkeit || 'Pilot',
      };

      if (!unterschriftPng) {
        setFormErrorMessages(['Unterschrift: Bitte unterschreibe im Unterschriftsfeld (ganz unten im Formular).']);
        return;
      }
      (pdfData as unknown as Record<string, string>).unterschrift_png = unterschriftPng;
      setLetzteAusweisDaten(pdfData as unknown as Record<string, string>);
      setAusweisSendeErgebnis(null);
      setAusweisSendeFehler(null);

      const [antragBytes, anlage4bBytes] = await erzeugeFlughafenPdfs(keycloak.token, pdfData);
      const jsZip = new JSZip();
      const namePrefix = sanitizeFilename(ausweisFamname.trim() + '_' + ausweisVorname.trim());
      jsZip.file('ANTRAG_' + namePrefix + '.pdf', antragBytes);
      jsZip.file('ANLAGE4B_' + namePrefix + '.pdf', anlage4bBytes);

      const blob = await jsZip.generateAsync({ type: 'blob' });
      setJsonUrl(URL.createObjectURL(blob));
      setDataObject({ vorname: ausweisVorname.trim(), famname: ausweisFamname.trim() });
      setShowDialog(true);
      setFormErrorMessages([]);
    } catch (err) {
      setFormErrorMessages(['PDF-Generierung fehlgeschlagen: ' + (err as Error).message]);
    }
  };

  const handleAusweisSenden = async () => {
    if (!letzteAusweisDaten) return;
    setAusweisSendet(true);
    setAusweisSendeFehler(null);
    try {
      const r = await flughafenSenden(keycloak.token, letzteAusweisDaten);
      setAusweisSendeErgebnis(
        'Deine Unterlagen sind bei der Ausweisstelle' + (r.cc ? ' — Kopie an ' + r.cc + '.' : '.'),
      );
    } catch (err) {
      setAusweisSendeFehler('Versand fehlgeschlagen: ' + (err as Error).message);
    } finally {
      setAusweisSendet(false);
    }
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    const form = event.currentTarget;
    event.preventDefault();
    event.stopPropagation();
    try {
      await handleSubmitInner(form);
    } catch (err) {
      // NIE stumm sterben (iOS zeigt sonst gar nichts): jeden technischen
      // Fehler sichtbar in die Fehlerbox schreiben.
      setFormErrorMessages(['Technischer Fehler beim Absenden — bitte Fabian melden: ' + ((err as Error)?.message || String(err))]);
      setTimeout(() => errorSummaryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
    }
  };

  const handleSubmitInner = async (form: HTMLFormElement) => {
    let correct = true;

    const newErrorMessages: string[] = [];

    if (form.geschlecht.value < 0) {
      form.geschlecht.setCustomValidity('Bitte Geschlecht auswählen');
      setGeschlechtInvalidMessage('Bitte Geschlecht auswählen');
      newErrorMessages.push('Persönliche Daten: Bitte Geschlecht auswählen');
      correct = false;
    }

    if (form.staat_1.value === '') {
      form.staat_1.setCustomValidity('Bitte Staatsangehörigkeit auswählen');
      setStaat1Invalidity('Bitte Staatsangehörigkeit auswählen');
      newErrorMessages.push('Persönliche Daten: Bitte Staatsangehörigkeit auswählen');
      correct = false;
    }

    // Geburtsname = NUR der Nachname bei der Geburt. Fall Eberhart 24.09.2026:
    // „Anna Eberhart" im Feld -> Behörde wies die Freigabe zurück.
    const gebnameVal = ((form.gebname.value as string) || '').trim();
    const vornameTok = ((form.vorname.value as string) || '').trim().split(/\s+/)[0]?.toLowerCase() || '';
    if (gebnameVal && vornameTok && gebnameVal.toLowerCase().split(/[\s-]+/).includes(vornameTok)) {
      form.gebname.setCustomValidity('Nur der Nachname bei der Geburt, ohne Vornamen');
      newErrorMessages.push('Persönliche Daten: Der Geburtsname darf den Vornamen nicht enthalten — nur der Nachname laut Geburtsurkunde (z. B. „Huber"). Leer lassen, wenn er gleich ist wie der Familienname.');
      correct = false;
    } else {
      form.gebname.setCustomValidity('');
    }

    if (docForm.current!.art.value < 0) {
      docForm.current!.art.setCustomValidity('Bitte Dokumentart auswählen');
      setDocArtInvalidMessage('Bitte Dokumentart auswählen');
      newErrorMessages.push('Identitätsdokument: Bitte Dokumentart auswählen');
      correct = false;
    } else {
      docForm.current!.art.setCustomValidity('');
      setDocArtInvalidMessage('');
    }

    if (wohnsitze.length === 0) {
      newErrorMessages.push('Wohnsitze: Keine Wohnsitze angegeben. Wohnsitze lückenlos für die letzen 5 Jahre angeben.');
      setWohnsitzeHasErrors(true);
    }

    if (arbeiten.length === 0) {
      newErrorMessages.push('Beschäftigungen: Keine Beschäftigungsverhältnisse angegeben. Lückenlos für die letzen 5 Jahre angeben.');
      setArbeitenHasErrors(true);
    }

    // Arbeitslos (art 2) braucht eine Angabe: beim AMS gemeldet — oder eine
    // kurze Begründung, warum nicht (sonst weist die Behörde nach).
    for (const a of arbeiten) {
      // Bestand (mit USP-id): wie von der Behörde akzeptiert. Neu: zwingend AMS.
      const ams = a.id ? (a.taetigkeit || '').trim() !== '' : /\bAMS\b/i.test(a.taetigkeit || '');
      if (a.art === '2' && !ams) {
        newErrorMessages.push('Beschäftigungen: Bei „Arbeitslos" muss im Feld Tätigkeit „beim AMS gemeldet" stehen. Nicht beim AMS gemeldet? Dann „Vereinsmitgliedschaft" (Flugsportzentrum Tirol) wählen.');
        setArbeitenHasErrors(true);
        break;
      }
    }

    // Ein OFFENER Eintrag „nicht zutreffend (Lücke)" heißt: aktueller Status
    // unbekannt — die Behörde weist das ab (Moosbrugger, zweimal, 10/2026).
    // Eine Lücke ist nur als abgeschlossener Zeitraum zwischen zwei Phasen ok.
    for (const a of arbeiten) {
      if (a.art === '4' && !a.bis) {
        newErrorMessages.push('Beschäftigungen: Dein aktueller Eintrag steht auf „nicht zutreffend (Lücke)". Die Behörde braucht deinen jetzigen Status: Ausbildung/Studium, Beschäftigung, Arbeitslos (beim AMS gemeldet), Zivil-/Wehrdienst, Pension, Selbständig — oder als Minimum „Vereinsmitgliedschaft" (Flugsportzentrum Tirol).');
        setArbeitenHasErrors(true);
        break;
      }
    }
    // NEUE Einträge (ohne USP-id) dürfen weder „nicht zutreffend (Lücke)" noch
    // den Platzhalter „Keine Angaben" tragen (Fabian, 23.10.2026). Bestands-
    // einträge aus dem USP bleiben, wie die Behörde sie akzeptiert hat.
    for (const a of arbeiten) {
      if (a.id) continue;
      if (a.art === '4') {
        newErrorMessages.push('Beschäftigungen: „nicht zutreffend (Lücke)" ist nicht mehr zulässig — bitte die Zeit als Ausbildung, Beschäftigung, Arbeitslos (beim AMS gemeldet), Selbständig, Pension oder „Vereinsmitgliedschaft" (Flugsportzentrum Tirol) eintragen.');
        setArbeitenHasErrors(true);
        break;
      }
      if (/keine\s*angabe/i.test(a.organisation || '') || /keine\s*angabe/i.test(a.taetigkeit || '')) {
        newErrorMessages.push('Beschäftigungen: „Keine Angaben" ist kein gültiger Eintrag — bitte Organisation und Tätigkeit konkret benennen.');
        setArbeitenHasErrors(true);
        break;
      }
    }

    // MRZ-Prüfziffern-Falle: Dokumentnummer von der RÜCKSEITE (MRZ, mit
    // angehängter Prüfziffer) statt von der Vorderseite eingegeben.
    // Beispiel AT-PA: Vorderseite PA1477562, MRZ PA14775627 (Prüfziffer 7).
    {
      const nummerVal = ((docForm.current?.nummer as HTMLInputElement | undefined)?.value || '').toUpperCase().trim();
      const verdaechtig =
        (selectedDocArt === '2' && /^PA\d{8}$/.test(nummerVal)) ||
        (selectedDocArt === '1' && /^[A-Z]\d{8}$/.test(nummerVal));
      if (verdaechtig && icaoCheckDigit(nummerVal.slice(0, -1)) === parseInt(nummerVal.slice(-1), 10)) {
        newErrorMessages.push(
          `Identitätsdokument: Die Nummer „${nummerVal}" stammt vermutlich aus der MRZ-Zeile auf der RÜCKSEITE (die letzte Ziffer ist eine Prüfziffer). Bitte die Nummer von der VORDERSEITE eingeben — vermutlich „${nummerVal.slice(0, -1)}". Falls die Nummer wirklich so auf der Vorderseite steht, melde dich bitte bei der Vereinsverwaltung.`,
        );
      }
    }


    if (form.checkValidity() === true && docForm.current?.checkValidity() === true &&
      (strafregisterForm.current === null || strafregisterForm.current.checkValidity() === true)) {

      const startMomentForChecks = moment().add(-5, 'year');
      wohnsitze.sort((a, b) => a.von.getTime() - b.von.getTime());
      let aktuellesWohnsitzDatum = startMomentForChecks;
      let wohnsitzOpenEnd = false;
      for (const w of wohnsitze) {
        if (aktuellesWohnsitzDatum.isBefore(moment(w.von).add(-1, 'days'))) {
          newErrorMessages.push('Wohnsitze: Lücke zwischen ' + aktuellesWohnsitzDatum.format(FULL_DATE_FORMAT) + ' und ' + moment(w.von).format(FULL_DATE_FORMAT));
          setWohnsitzeHasErrors(true);
          break;
        }
        if (w.bis === undefined) {
          wohnsitzOpenEnd = true;
          break;
        }
        aktuellesWohnsitzDatum = moment(w.bis);
      }
      if (!wohnsitzOpenEnd && moment().add(-1, 'days').isAfter(aktuellesWohnsitzDatum)) {
        newErrorMessages.push('Wohnsitze: Lücke zwischen ' + aktuellesWohnsitzDatum.format(FULL_DATE_FORMAT) + ' und ' + moment().format(FULL_DATE_FORMAT));
        setWohnsitzeHasErrors(true);
      }
      // Aktueller Wohnsitz MUSS ein offenes Enddatum haben (Offenliste
      // ws_aktuell). Ein Eintrag „bis heute" erzeugt keine Lücke und rutschte
      // deshalb durch die Prüfung oben durch (Fabian, 07.08.2026).
      if (wohnsitze.length > 0 && !hatOffenenEintrag(wohnsitze.map((w) => ({ von: w.von, bis: w.bis })))) {
        newErrorMessages.push('Wohnsitze: Dein aktueller Wohnsitz braucht ein offenes Enddatum — bitte beim jüngsten Eintrag das Feld „bis" leer lassen.');
        setWohnsitzeHasErrors(true);
      }

      arbeiten.sort((a, b) => a.von.getTime() - b.von.getTime());
      let aktuellesArbeitsDatum = startMomentForChecks;
      let arbeitenOpenEnd = false;
      for (const a of arbeiten) {
        if (aktuellesArbeitsDatum.isBefore(moment(a.von).add(-28, 'days'))) {
          newErrorMessages.push('Beschäftigungen: Lücke zwischen ' + aktuellesArbeitsDatum.format(MONTH_DATE_FORMAT) + ' und ' + moment(a.von).format(MONTH_DATE_FORMAT));
          setArbeitenHasErrors(true);
          break;
        }
        if (a.bis === undefined) {
          arbeitenOpenEnd = true;
          break;
        }
        // Use max end date to handle overlapping/parallel work periods
        const bisMoment = moment(a.bis);
        if (bisMoment.isAfter(aktuellesArbeitsDatum)) {
          aktuellesArbeitsDatum = bisMoment;
        }
      }

      if (!arbeitenOpenEnd && moment().add(-28, 'days').isAfter(aktuellesArbeitsDatum)) {
        newErrorMessages.push('Beschäftigungen: Lücke zwischen ' + aktuellesArbeitsDatum.format(MONTH_DATE_FORMAT) + ' und ' + moment().format(MONTH_DATE_FORMAT) + '. Max. 28 Tage Lücke erlaubt.');
        setArbeitenHasErrors(true);
      }
      // Aktuelle Beschäftigung MUSS ein offenes Enddatum haben (Offenliste
      // bv_aktuell) — gleiche Falle wie beim Wohnsitz: „bis heute" erzeugt
      // keine Lücke und rutschte durch.
      if (arbeiten.length > 0 && !hatOffenenEintrag(arbeiten.map((a) => ({ von: a.von, bis: a.bis })))) {
        newErrorMessages.push('Beschäftigungen: Dein aktuelles Beschäftigungsverhältnis braucht ein offenes Enddatum — bitte beim jüngsten Eintrag das Feld „bis" leer lassen (auch bei „Arbeitslos" oder „nicht zutreffend").');
        setArbeitenHasErrors(true);
      }

    }
    else {
      correct = false;
      // Collect native validation errors from personal data form
      if (form.checkValidity() === false) {
        const fields: Record<string, string> = {
          famname: 'Familienname', vorname: 'Vorname', gebdatum: 'Geburtsdatum',
          geb_ort: 'Geburtsort', geb_ort_land: 'Geburtsort Land',
          vorname_m: 'Vorname Mutter', vorname_v: 'Vorname Vater',
          kontakt_tel: 'Kontakt-Telefon', kontakt_mail: 'Kontakt-Mail',
        };
        for (const [name, label] of Object.entries(fields)) {
          const el = form.elements.namedItem(name) as HTMLInputElement | null;
          if (el && !el.validity.valid) {
            newErrorMessages.push('Persönliche Daten: ' + label + ' fehlt oder ist ungültig');
          }
        }
      }
      if (docForm.current?.checkValidity() === false) {
        const docFields: Record<string, string> = {
          file: 'Dokument-Datei', nummer: 'Dokumentnummer', ausstellung: 'Ausstellungsdatum',
        };
        for (const [name, label] of Object.entries(docFields)) {
          const el = docForm.current.elements.namedItem(name) as HTMLInputElement | null;
          if (el && !el.validity.valid) {
            newErrorMessages.push('Identitätsdokument: ' + label + ' fehlt oder ist ungültig');
          }
        }
      }
      if (strafregisterForm.current !== null && strafregisterForm.current.checkValidity() === false) {
        // WICHTIG: Ohne diesen Hinweis glauben Mitglieder, ihre übrigen
        // Eingaben (z.B. der neue Reisepass) seien gespeichert — sind sie
        // nicht, der Antrag wird ohne Strafregister nie eingereicht (Fälle
        // Moroder/Peer/Obererlacher 08/2026).
        newErrorMessages.push(
          'Strafregisterauszüge: Bitte alle Pflichtfelder für nicht-österreichische Länder ausfüllen. '
          + 'Achtung: Deine Eingaben werden erst mit dem Absenden übernommen — nichts ist bisher gespeichert! '
          + 'Hast du den Auszug noch nicht: Lade deinen neuen Ausweis jetzt schon über die Startseiten-Kachel '
          + '„Dokument nachreichen" hoch und stelle den Antrag fertig, sobald der Auszug da ist.');
      }
    }

    setFormErrorMessages(newErrorMessages);

    if (newErrorMessages.length > 0 || !correct) {
      setTimeout(() => { if (errorSummaryRef.current?.scrollIntoView) errorSummaryRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' }); }, 100);
    }

    if (correct && newErrorMessages.length === 0) {
      setArbeitenHasErrors(false);
      setWohnsitzeHasErrors(false);
      // Leerzeichen-Ränder abschneiden, bevor irgendetwas das Formular
      // verlässt (Trailing Spaces stören die behördliche Vorprüfung).
      const dataObject = trimTiefe(createJson(form));
      const json = JSON.stringify(dataObject);
      setDataObject(dataObject[0]);
      const blob = new Blob([json], { type: 'application/json' });

      const jsZip = new JSZip();
      jsZip.file('zuep.json', blob);
      // Use ref to avoid stale closure with async handler
      const currentLoadedFiles = loadedFilesRef.current;

      if (docForm.current) {
        // Bei hinterlegtem Bestandsdokument (kein neuer Upload nötig) wird das
        // file-Input gar nicht gerendert -- Zugriff muss optional sein.
        const fileInput = docForm.current.file as HTMLInputElement | undefined;
        if (fileInput?.files && fileInput.files.length > 0) {
          const file = fileInput.files[0];
          jsZip.file(sanitizeFilename(file.name), file);
        } else if (loadedDocName && currentLoadedFiles.has(loadedDocName)) {
          jsZip.file(sanitizeFilename(loadedDocName), currentLoadedFiles.get(loadedDocName)!);
        }

        // Rückseite only for Personalausweis (art=2)
        if (selectedDocArt === '2') {
          const file2Input = docForm.current.file2 as HTMLInputElement | undefined;
          if (file2Input?.files && file2Input.files.length > 0) {
            const file2 = file2Input.files[0];
            const vorder = fileInput?.files?.[0];
            if (vorder && vorder.name === file2.name && vorder.size === file2.size) {
              setFormErrorMessages(['Personalausweis: Vorder- und Rückseite sind dieselbe Datei — bitte beide Seiten einzeln fotografieren und hochladen.']);
              setTimeout(() => errorSummaryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
              return;
            }
            jsZip.file(sanitizeFilename(file2.name), file2);
          } else if (loadedDoc2Name && currentLoadedFiles.has(loadedDoc2Name)) {
            jsZip.file(sanitizeFilename(loadedDoc2Name), currentLoadedFiles.get(loadedDoc2Name)!);
          }
        }
      }

      if (strafregisterForm.current != null) {
        const formData = new FormData(strafregisterForm.current);
        for (const key of formData.keys()) {
          const d = formData.get(key);
          if (d instanceof File && d.size > 0) {
            const zipName = sanitizeFilename(d.name.startsWith(key + '_') ? d.name : key + '_' + d.name);
            jsZip.file(zipName, d);
          }
        }
      }

      // Include any loaded files from ZIP that weren't overridden by new file inputs
      for (const [name, loadedFile] of currentLoadedFiles) {
        const safeName = sanitizeFilename(name);
        if (!jsZip.files[safeName]) {
          jsZip.file(safeName, loadedFile);
        }
      }

      // Generate diff file if data was loaded from a previous year
      if (loadedOriginalData) {
        jsZip.file('aenderungen.txt', generateDiff(loadedOriginalData, dataObject[0]));
      }

      // ANTRAG & ANLAGE 4B: beim Erstantrag PFLICHT — die Ausweisstelle
      // übernimmt neue Fälle erst mit den unterschriebenen Formularen
      // (Security/Lisa, 16.09.2026). Fehler brechen den Antrag ab, statt
      // still geschluckt zu werden (so fehlten die PDFs bei allen vier
      // September-Erstanträgen).
      if (appMode === 'neuantrag') {
        if (!unterschriftPng) {
          setFormErrorMessages(['Unterschrift: Bitte unterschreibe im Unterschriftsfeld (Abschnitt ANTRAG & ANLAGE 4B, weiter unten).']);
          setTimeout(() => errorSummaryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
          return;
        }
      }
      if (appMode === 'neuantrag') try {
        const d = dataObject[0] as any;
        // Find current address (first Wohnsitz with no end date)
        const currentWohnsitz = wohnsitze.find(w => !w.bis) || wohnsitze[wohnsitze.length - 1];
        const pdfData: PdfFormData = {
          vorname: d.vorname,
          famname: d.famname,
          nationalitaet: StatesAlpha2List.ALPHA2CODES[d.staat_1] || d.staat_1,
          gebdatum: d.gebdatum ? moment(d.gebdatum).format(FULL_DATE_FORMAT) : '',
          geb_ort_plz: d.geb_ort_plz || '',
          geb_ort: d.geb_ort,
          geb_ort_land: StatesAlpha2List.ALPHA2CODES[d.geb_ort_land] || d.geb_ort_land,
          geschlecht: GESCHLECHT_LABEL[d.geschlecht] || '',
          kontakt_tel: d.kontakt_tel,
          kontakt_mail: d.kontakt_mail,
          adresse_plz: currentWohnsitz?.plz || '',
          adresse_ort: currentWohnsitz?.ort || '',
          adresse_strasse: currentWohnsitz
            ? [currentWohnsitz.strasse, currentWohnsitz.hausnummer].filter(Boolean).join(' ')
            : '',
          rechnung_firma: rechnungFirma,
          rechnung_strasse: rechnungStrasse,
          rechnung_plzort: rechnungPlzort,
          taetigkeit: d._freigabe?.info || 'Pilot',
        };
        (pdfData as unknown as Record<string, string>).unterschrift_png = unterschriftPng as string;
        setLetzteAusweisDaten(pdfData as unknown as Record<string, string>);
        setAusweisSendeErgebnis(null);
        setAusweisSendeFehler(null);

        const [antragBytes, anlage4bBytes] = await erzeugeFlughafenPdfs(keycloak.token, pdfData);
        jsZip.file('ANTRAG_' + sanitizeFilename(d.famname + '_' + d.vorname) + '.pdf', antragBytes);
        jsZip.file('ANLAGE4B_' + sanitizeFilename(d.famname + '_' + d.vorname) + '.pdf', anlage4bBytes);
      } catch (err) {
        setFormErrorMessages(['Flughafen-Formulare: ' + (err as Error).message]);
        setTimeout(() => errorSummaryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
        return;
      }

      jsZip.generateAsync({ type: 'blob' }).then((blob) => {
        const url = URL.createObjectURL(blob);
        setJsonUrl(url);
        setAntragBlob(blob);
        setEinreichRichtig(false);
        setEinreichKosten(false);
        setEinreichErgebnis(null);
      });




      /*var a = document.createElement('a');
      a.href = url;
      a.download = 'backup.json';
      a.textContent = 'Download backup.json';

      document.getElementById('json').appendChild(a);*/
      setShowDialog(true);
    }

    setValidated(true);
    setDocFormValidated(true);
  };

  const createJson = (form: HTMLFormElement): Array<Object> => {

    const adressArray: Array<Object> = [];

    for (let w of wohnsitze) {
      const adresse: any = {
        art: 0,
        land: w.land.toUpperCase(),
        plz: w.plz,
        ort: w.ort,
        strasse: w.strasse,
        zusatz: w.hausnummer,
        von: moment(w.von).format(JSON_DATE_FORMAT),
        bis: w.bis ? moment(w.bis).format(JSON_DATE_FORMAT) : null
      };
      if (w.id !== undefined) adresse.id = w.id;
      adressArray.push(adresse);
    }

    const workArray: Array<Object> = [];
    for (let w of arbeiten) {
      const arbeit: any = {
        art: parseInt(w.art),
        name: w.organisation,
        info: w.taetigkeit,
        land: w.land?.toUpperCase(),
        von: moment(w.von).format(JSON_DATE_FORMAT),
        bis: w.bis ? moment(w.bis).format(JSON_DATE_FORMAT) : null
      }
      // id gesetzt = bestehender USP-Datensatz wird AKTUALISIERT (kein Duplikat).
      if (w.id !== undefined) arbeit.id = w.id;
      workArray.push(arbeit);
    }

    const dokumentArray: Array<Object> = [];
    if (docForm.current) {
      // file-Input existiert bei Bestandsdokument ohne Ersetzen NICHT im DOM
      const fileInput = docForm.current.file as HTMLInputElement | undefined;
      const file = fileInput?.files?.[0];
      const loadedFile = loadedDocName ? loadedFilesRef.current.get(loadedDocName) : undefined;
      const filename = sanitizeFilename(file?.name ?? loadedFile?.name ?? loadedDocName ?? '');
      const rawMime = file?.type || loadedDocMime || '';
      if (filename) {
        const doc = {
          art: parseInt(docForm.current.art.value),
          ausstellung: moment(docForm.current.ausstellung.value).format(JSON_DATE_FORMAT),
          // Echtes Ablaufdatum vom Dokument - beendet das Schaetzen.
          gueltig_bis: docForm.current.gueltig_bis?.value
            ? moment(docForm.current.gueltig_bis.value).format(JSON_DATE_FORMAT) : null,
          info: null,
          nummer: docForm.current.nummer.value,
          name: filename,
          mime: normalizeMime(rawMime),
          upload: true
        };
        dokumentArray.push(doc);
      }

      // Rückseite only for Personalausweis (art=2)
      const file2Input = selectedDocArt === '2' ? docForm.current.file2 as HTMLInputElement | undefined : undefined;
      const file2 = file2Input?.files?.[0];
      const loadedFile2 = selectedDocArt === '2' && loadedDoc2Name ? loadedFilesRef.current.get(loadedDoc2Name) : undefined;
      if (file2 || loadedFile2) {
        const filename2 = sanitizeFilename(file2?.name ?? loadedFile2?.name ?? loadedDoc2Name ?? '');
        const rawMime2 = file2?.type || loadedDoc2Mime || '';
        const doc2 = {
          art: parseInt(docForm.current.art.value),
          ausstellung: moment(docForm.current.ausstellung.value).format(JSON_DATE_FORMAT),
          // Echtes Ablaufdatum vom Dokument - beendet das Schaetzen.
          gueltig_bis: docForm.current.gueltig_bis?.value
            ? moment(docForm.current.gueltig_bis.value).format(JSON_DATE_FORMAT) : null,
          info: null,
          nummer: docForm.current.nummer.value,
          name: filename2,
          mime: normalizeMime(rawMime2),
          upload: true
        };
        dokumentArray.push(doc2);
      }
    }

    // Strafregisterauszüge are NOT included in dokument[] because the JSON schema
    // requires a wohnsitz (address ID) reference for art=0, which is not known at
    // import time for new persons. Instead, metadata is stored in _strafregister
    // for later use (auto-fill USP portal). Files are included in the ZIP.
    const strafregisterMeta: Array<any> = [];
    if (strafregisterForm.current) {
      const formData = new FormData(strafregisterForm.current);
      const processedLands = new Set<string>();
      for (const key of formData.keys()) {
        const match = key.match(/^strafregister_([A-Z]{2})_ausstellung$/);
        if (match && !processedLands.has(match[1])) {
          const land = match[1];
          processedLands.add(land);
          const fileInput = strafregisterForm.current.querySelector(`input[name="strafregister_${land}"]`) as HTMLInputElement | null;
          const strafFile = fileInput?.files?.[0];
          // Find loaded file by prefix match (files stored as e.g. "strafregister_DE_scan.pdf")
          const strafPrefix = `strafregister_${land}`;
          let loadedStrafFile: File | undefined;
          for (const [fname, fobj] of loadedFilesRef.current) {
            if (fname.startsWith(strafPrefix)) { loadedStrafFile = fobj; break; }
          }
          // Optionale Kachel (nur Auslands-Beschäftigung) ohne Upload:
          // Land komplett überspringen, statt leere Metadaten zu erzeugen.
          if (!strafFile && !loadedStrafFile) continue;
          const fileName = sanitizeFilename(strafFile?.name
            ? (strafFile.name.startsWith(`strafregister_${land}_`) ? strafFile.name : `strafregister_${land}_${strafFile.name}`)
            : loadedStrafFile?.name ?? '');
          const eintragVal = formData.get(`strafregister_${land}_eintragungen`);
          const wohnsitzIdx = formData.get(`strafregister_${land}_wohnsitz`);
          const strafMime = strafFile?.type || loadedStrafFile?.type || '';

          strafregisterMeta.push({
            land: land,
            ausstellung: moment(formData.get(`strafregister_${land}_ausstellung`) as string).format(JSON_DATE_FORMAT),
            eintragung: eintragVal === 'ja' ? 1 : 0,
            wohnsitz_index: wohnsitzIdx !== null ? parseInt(wohnsitzIdx as string) : null,
            name: fileName,
            mime: normalizeMime(strafMime),
          });
        }
      }
    }

    const obj: any = {
      // id gesetzt = USP-Update derselben Person; null = Neuanlage (Erstantrag).
      id: loadedOriginalData?.id ?? null,
      unternehmen: 49,
      titel: form.titel.value || null,
      vorname: form.vorname.value,
      famname: (form.famname.value as string).trim(),
      gebname: form.gebname.value || null,
      geschlecht: parseInt(form.geschlecht.value),
      gebdatum: form.gebdatum.value,
      geb_ort: form.geb_ort.value,
      geb_ort_plz: form.geb_ort_plz.value || null,
      geb_ort_land: form.geb_ort_land.value,
      vorname_m: form.vorname_m.value,
      vorname_v: form.vorname_v.value,
      staat_1: form.staat_1.value.toUpperCase(),
      staat_2: form.staat_2.value?.toUpperCase() || null,
      kontakt_tel: form.kontakt_tel.value,
      kontakt_mail: form.kontakt_mail.value,
      zustimmung: 1,
      suep: null,
      adresse: adressArray,
      beschaeftigung: workArray,
      dokument: dokumentArray
    };

    // Strafregister metadata for later USP auto-fill
    if (strafregisterMeta.length > 0) {
      obj._strafregister = strafregisterMeta;
    }

    // Antrag data: always ZÜP-Erweitert, Flughafenausweis
    obj._antrag = {
      art: 0,
      form: 1,
      grund: '11',
    };

    obj._freigabe = {
      airport_u_id: 49,
      info: 'Pilot',
    };

    if (rechnungFirma || rechnungStrasse || rechnungPlzort) {
      obj._rechnung = {
        firma: rechnungFirma,
        strasse: rechnungStrasse,
        plzort: rechnungPlzort,
      };
    }

    return [obj];
  }

  const checkFile = (e: ChangeEvent<HTMLInputElement>, invaliditySetter: (s: string) => void,
                     infoSetter: (s: string) => void) => {
    checkFileElement(e.target, invaliditySetter, infoSetter);
  }

  const checkFileElement = async (target: HTMLInputElement, invaliditySetter: (s: string) => void,
                                  infoSetter: (s: string) => void) => {
    if (target.files === null || target.files.length === 0) return;
    const file = target.files[0];
    infoSetter('');
    // Immer durch den Kompressor: verkleinert zu große Bilder (Behörden-
    // Limit 2 MB) UND wandelt iPhone-Formate (HEIC) nach JPEG — beides
    // scheiterte sonst still (Fall Moroder 03.09.2026).
    const ergebnis = await komprimiereBild(file);
    if (ergebnis.problem) {
      target.setCustomValidity('Datei zu groß');
      invaliditySetter(ergebnis.problem);
      return;
    }
    if (ergebnis.file !== file) {
      const dt = new DataTransfer();
      dt.items.add(ergebnis.file);
      target.files = dt.files;
      infoSetter(ergebnis.verkleinert
        ? `Foto automatisch verkleinert: ${(file.size / 1e6).toFixed(1)} MB → ${(ergebnis.file.size / 1e6).toFixed(1)} MB`
        : 'Foto automatisch in JPEG umgewandelt');
    }
    target.setCustomValidity('');
    invaliditySetter('');
  }

  const wohnsitzAdd = (wohnsitz: Wohnsitz) => {
    // Auto-close previous open-ended Wohnsitz: set its bis to the day before the new von
    setWohnsitze((prev) => {
      const updated = prev.map(w => {
        if (!w.bis) {
          const newBis = moment(wohnsitz.von).add(-1, 'days').toDate();
          return { ...w, bis: newBis };
        }
        return w;
      });
      return [...updated, wohnsitz];
    });
    setShowWohnsitzAdd(false);
    setWohnsitzeHasErrors(false);
  };

  const editWohnsitz = (index: number, updated: Wohnsitz) => {
    setWohnsitze((prev) => prev.map((w, i) => i === index ? updated : w));
  };

  const arbeitAdd = (arbeit: Arbeit) => {
    setArbeiten((prev) => [...prev, arbeit]);
    setShowArbeitAdd(false);
    setArbeitenHasErrors(false);
  }

  const editArbeit = (index: number, updated: Arbeit) => {
    setArbeiten((prev) => prev.map((a, i) => i === index ? updated : a));
  };

  const deleteWohnsitz = (index: number) => {
    setWohnsitze(wohnsitze.filter((_a, i) => i !== index));
  }
  const deleteArbeit = (index: number) => {
    setArbeiten(arbeiten.filter((_a, i) => i !== index));
  }

  const form = useRef<HTMLFormElement>(null);
  const strafregisterForm = useRef<HTMLFormElement>(null);
  const docForm = useRef<HTMLFormElement>(null);
  const loadFileInput = useRef<HTMLInputElement>(null);
  const errorSummaryRef = useRef<HTMLDivElement>(null);

  // Helper: inject a File into a file input via DataTransfer (if not already set)
  const injectFileIntoInput = (input: HTMLInputElement, file: File): boolean => {
    if (input.files && input.files.length > 0 && input.files[0].name === file.name) return true;
    try {
      const dt = new DataTransfer();
      dt.items.add(file);
      input.files = dt.files;
      return input.files.length > 0;
    } catch (_) { return false; }
  };

  // Re-inject loaded files into file inputs after EVERY render.
  // React re-renders can recreate DOM nodes and wipe programmatically set files.
  // This effect ensures files always stay populated.
  useEffect(() => {
    const files = loadedFilesRef.current;
    if (files.size === 0) return;

    const rafId = requestAnimationFrame(() => {
      // Identity document file(s)
      if (docForm.current) {
        const df = docForm.current as any;
        if (loadedDocName) {
          const f = files.get(loadedDocName);
          if (f && df.file) injectFileIntoInput(df.file as HTMLInputElement, f);
        }
        if (loadedDoc2Name) {
          const f2 = files.get(loadedDoc2Name);
          if (f2 && df.file2) injectFileIntoInput(df.file2 as HTMLInputElement, f2);
        }
      }

      // Strafregister files
      if (strafregisterForm.current && loadedStrafregister.length > 0) {
        const formEl = strafregisterForm.current;
        for (const sr of loadedStrafregister) {
          const land = sr.land;
          if (!land) continue;

          // Fill form field values
          const ausstellungInput = formEl.querySelector(`input[name="strafregister_${land}_ausstellung"]`) as HTMLInputElement | null;
          if (ausstellungInput && sr.ausstellung && !ausstellungInput.value) ausstellungInput.value = sr.ausstellung;
          const eintragSelect = formEl.querySelector(`select[name="strafregister_${land}_eintragungen"]`) as HTMLSelectElement | null;
          if (eintragSelect && eintragSelect.value === 'nein' && sr.eintragung === 1) eintragSelect.value = 'ja';
          const wohnsitzSelect = formEl.querySelector(`select[name="strafregister_${land}_wohnsitz"]`) as HTMLSelectElement | null;
          if (wohnsitzSelect && sr.wohnsitz !== undefined && sr.wohnsitz !== null) wohnsitzSelect.value = sr.wohnsitz.toString();

          // Inject file (exact match then prefix match)
          if (sr.name) {
            let strafFile = files.get(sr.name);
            if (!strafFile) {
              const prefix = `strafregister_${land}`;
              for (const [fname, fobj] of files) {
                if (fname.startsWith(prefix)) { strafFile = fobj; break; }
              }
            }
            if (strafFile) {
              const fileInput = formEl.querySelector(`input[name="strafregister_${land}"]`) as HTMLInputElement | null;
              if (fileInput) injectFileIntoInput(fileInput, strafFile);
            }
          }
        }
      }
    });
    return () => cancelAnimationFrame(rafId);
  }); // No dependency array — runs after every render to survive re-renders

  const loadJson = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        let jsonText: string;
        let zip: JSZip | null = null;
        const filesMap = new Map<string, File>();

        if (file.name.endsWith('.zip')) {
          zip = await JSZip.loadAsync(e.target!.result as ArrayBuffer);
          const jsonFile = zip.file('zuep.json');
          if (!jsonFile) { alert('Keine zuep.json in ZIP gefunden'); return; }
          // Parse JSON first to get MIME types, then extract files
          jsonText = await jsonFile.async('text');
        } else {
          jsonText = e.target!.result as string;
          loadedFilesRef.current = new Map<string, File>();
        }

        const data = JSON.parse(jsonText);

        // Extract files from ZIP with correct MIME types from JSON metadata
        if (zip) {
          const d0 = Array.isArray(data) ? data[0] : data;
          const mimeMap = new Map<string, string>();
          if (d0 && Array.isArray(d0.dokument)) {
            for (const doc of d0.dokument) {
              if (doc.name && doc.mime) mimeMap.set(doc.name, doc.mime);
            }
          }
          const fileEntries = Object.entries(zip.files).filter(([name]) => name !== 'zuep.json' && !name.endsWith('/'));
          for (const [name, entry] of fileEntries) {
            const blob = await entry.async('blob');
            const mimeType = mimeMap.get(name) || blob.type || 'application/octet-stream';
            const loadedFile = new File([blob], name, { type: mimeType });
            filesMap.set(name, loadedFile);
          }
          loadedFilesRef.current = filesMap;
        }
        const d = Array.isArray(data) ? data[0] : data;
        if (!d) return;
        applyLoadedData(d);
      } catch (err) {
        alert('Fehler beim Laden der Datei: ' + (err as Error).message);
      }
    };

    if (file.name.endsWith('.zip')) {
      reader.readAsArrayBuffer(file);
    } else {
      reader.readAsText(file);
    }
    // Reset so the same file can be re-selected
    event.target.value = '';
  };

  /** Befüllt alle Formulare aus einem Datensatz im USP-Export-Format --
   * entweder aus der geladenen zuep.json (ZIP, loadJson) oder aus einem aus
   * `GET /api/me` konstruierten Objekt (automatische Vorbefüllung). */
  const applyLoadedData = (d: any) => {
        // Store original loaded data for diff generation
        setLoadedOriginalData(JSON.parse(JSON.stringify(d)));

        // Populate ausweis form fields (for ausweis-verlaengerung mode)
        if (appMode === 'ausweis-verlaengerung') {
          setAusweisVorname(d.vorname ?? '');
          setAusweisFamname(d.famname ?? '');
          setAusweisGebdatum(d.gebdatum ?? '');
          setAusweisGebOrt(d.geb_ort ?? '');
          setAusweisGebOrtPlz(d.geb_ort_plz ?? '');
          setAusweisGebOrtLand(d.geb_ort_land ?? '');
          setAusweisGeschlecht(d.geschlecht?.toString() ?? '-1');
          setAusweisNationalitaet(d.staat_1 ?? '');
          setAusweisTel(d.kontakt_tel ?? '');
          setAusweisMail(d.kontakt_mail ?? '');
          setAusweisTaetigkeit(d._freigabe?.info ?? 'Pilot');
          // Current address from loaded data
          if (Array.isArray(d.adresse)) {
            const currentAddr = d.adresse.find((a: any) => !a.bis) || d.adresse[d.adresse.length - 1];
            if (currentAddr) {
              setAusweisPlz(currentAddr.plz ?? '');
              setAusweisOrt(currentAddr.ort ?? '');
              setAusweisStrasse([currentAddr.strasse, currentAddr.zusatz].filter(Boolean).join(' '));
            }
          }
        }

        // Populate form fields only if the personal data form is rendered
        if (form.current) {
        const f = form.current as any;
        f.titel.value = d.titel ?? '';
        f.famname.value = d.famname ?? '';
        f.vorname.value = d.vorname ?? '';
        f.gebname.value = d.gebname ?? '';
        f.geschlecht.value = d.geschlecht?.toString() ?? '-1';
        f.gebdatum.value = d.gebdatum ?? '';
        f.geb_ort.value = d.geb_ort ?? '';
        f.geb_ort_plz.value = d.geb_ort_plz ?? '';
        f.geb_ort_land.value = d.geb_ort_land ?? '';
        f.vorname_m.value = d.vorname_m ?? '';
        f.vorname_v.value = d.vorname_v ?? '';
        f.staat_1.value = d.staat_1 ?? '';
        setState1(d.staat_1 ?? undefined);
        f.staat_2.value = d.staat_2 ?? '';
        f.kontakt_tel.value = d.kontakt_tel ?? '';
        f.kontakt_mail.value = d.kontakt_mail ?? '';
        } // end if (form.current)

        if (Array.isArray(d.adresse)) {
          setWohnsitze(d.adresse.map((a: any) => ({
            id: a.id,
            land: a.land ?? '',
            plz: a.plz ?? '',
            ort: a.ort ?? '',
            strasse: a.strasse ?? '',
            hausnummer: a.zusatz ?? '',
            von: new Date(a.von),
            bis: a.bis ? new Date(a.bis) : undefined,
          })));
        }

        if (Array.isArray(d.beschaeftigung)) {
          setArbeiten(d.beschaeftigung.map((b: any) => ({
            id: b.id,
            art: b.art?.toString() ?? '0',
            organisation: b.name ?? '',
            taetigkeit: b.info ?? '',
            land: b.land ?? 'AT',
            von: new Date(b.von),
            bis: b.bis ? new Date(b.bis) : undefined,
          })));
        }

        // Separate identity documents (art=1..5: Reisepass, Personalausweis,
        // Fremdenpass, Konventionspass, Identitätsausweis) from strafregister
        // (art=0). Ohne 3-5 gingen solche Dokumente beim Prefill verloren.
        if (Array.isArray(d.dokument)) {
          const idDocs = d.dokument.filter((doc: any) => doc.art >= 1 && doc.art <= 5);
          const strafDocs = d.dokument.filter((doc: any) => doc.art === 0);

          // Pre-fill identity document fields (only when form is rendered)
          if (idDocs.length > 0 && docForm.current) {
            const doc = idDocs[0];
            const df = docForm.current as any;
            const artVal = doc.art?.toString() ?? '-1';
            df.art.value = artVal;
            setSelectedDocArt(artVal);
            df.nummer.value = doc.nummer ?? '';
            df.ausstellung.value = doc.ausstellung ?? '';
            if (df.gueltig_bis) df.gueltig_bis.value = (doc as any).gueltig_bis ?? '';

            // Trigger pattern update for doc art
            if (doc.art === 1) {
              setDocNummerPattern('^[A-Za-z0-9]{7,9}$');
              setDocNummerHint('7-9 Zeichen, Buchstaben und Ziffern (z.B. U1234567, AA1234567, C01X00T47)');
              setDocNummerInvalidMessage('Reisepassnummer: 7-9 Buchstaben/Ziffern (z.B. AT: U1234567, IT: AA1234567, DE: C01X00T47)');
            } else if (doc.art === 2) {
              setDocNummerPattern('^[A-Za-z0-9]{6,10}$');
              setDocNummerHint('Nummer von der VORDERSEITE der Karte (z.B. PA1234567) — nicht aus der MRZ-Zeile auf der Rückseite (dort hängt eine Prüfziffer an).');
              setDocNummerInvalidMessage('Personalausweisnummer: 6-10 Buchstaben/Ziffern');
            } else if (doc.art >= 3 && doc.art <= 5) {
              setDocNummerPattern('^[A-Za-z0-9]{6,10}$');
              setDocNummerHint('Dokumentnummer wie auf dem Dokument angegeben (6-10 Zeichen).');
              setDocNummerInvalidMessage('Dokumentnummer: 6-10 Buchstaben/Ziffern');
            }

            // Check document validity (Reisepass & Personalausweis: 10 Jahre gültig)
            if (doc.ausstellung) {
              const ausstellungDate = moment(doc.ausstellung);
              // Steht das echte Ablaufdatum am Dokument, gilt es - sonst die
              // (nur naeherungsweise) 10-Jahres-Annahme.
              const echtesEnde = (doc as any).gueltig_bis;
              const validUntil = echtesEnde ? moment(echtesEnde) : ausstellungDate.clone().add(10, 'years');
              if (validUntil.isBefore(moment())) {
                const artName = ({1: 'Reisepass', 2: 'Personalausweis', 3: 'Fremdenpass', 4: 'Konventionspass', 5: 'Identitätsausweis'} as Record<number, string>)[doc.art] ?? 'Dokument';
                setDocExpiredWarning(artName + ' ist abgelaufen (ausgestellt am ' + ausstellungDate.format('DD.MM.YYYY') + ', gültig bis ' + validUntil.format('DD.MM.YYYY') + '). Bitte ein neues Dokument hochladen.');
              } else {
                setDocExpiredWarning('');
              }
            }

            // Store document name/mime references (for both ZIP and JSON loading)
            if (doc.name) {
              setLoadedDocName(doc.name);
              setLoadedDocMime(doc.mime ?? '');
            } else {
              setLoadedDocName('');
              setLoadedDocMime('');
            }
            if (idDocs.length > 1 && idDocs[1].name) {
              setLoadedDoc2Name(idDocs[1].name);
              setLoadedDoc2Mime(idDocs[1].mime ?? '');
            } else {
              setLoadedDoc2Name('');
              setLoadedDoc2Mime('');
            }

            // File injection into inputs is handled by the useEffect (runs after render)
          }

          // Load strafregister metadata from dokument[] entries with art=0
          if (strafDocs.length > 0) {
            setLoadedStrafregister(strafDocs.map((sd: any) => {
              // Extract land from filename pattern "strafregister_XX_..."
              const nameMatch = sd.name?.match(/^strafregister_([A-Z]{2})_/);
              const land = nameMatch ? nameMatch[1] : '';
              return {
                land: land,
                ausstellung: sd.ausstellung ?? '',
                eintragung: sd.eintragung ?? 0,
                wohnsitz: sd.wohnsitz,
                name: sd.name ?? ''
              };
            }));
          } else {
            setLoadedStrafregister([]);
          }
        }

        // Also load strafregister from _strafregister metadata (new format)
        if (Array.isArray(d._strafregister) && d._strafregister.length > 0) {
          setLoadedStrafregister(d._strafregister.map((sr: any) => ({
            land: sr.land ?? '',
            ausstellung: sr.ausstellung ?? '',
            eintragung: sr.eintragung ?? 0,
            wohnsitz: sr.wohnsitz_index,
            name: sr.name ?? ''
          })));
        }

        // Restore billing (Rechnungsempfänger) fields
        if (d._rechnung) {
          setRechnungFirma(d._rechnung.firma ?? '');
          setRechnungStrasse(d._rechnung.strasse ?? '');
          setRechnungPlzort(d._rechnung.plzort ?? '');
        } else if (Array.isArray(d.adresse) && d.adresse.length > 0) {
          // Default: letzte eingetragene (aktuelle) Adresse + Name des
          // Mitglieds als Rechnungsempfänger -- bleibt frei abänderbar.
          const aktuell = d.adresse.find((a: any) => !a.bis) || d.adresse[d.adresse.length - 1];
          const name = [d.titel, d.vorname, d.famname].filter(Boolean).join(' ');
          setRechnungFirma((prev) => prev || name);
          setRechnungStrasse((prev) => prev || [aktuell.strasse, aktuell.zusatz].filter(Boolean).join(' '));
          setRechnungPlzort((prev) => prev || [aktuell.plz, aktuell.ort].filter(Boolean).join(' '));
        }

        setValidated(false);
        setDocFormValidated(false);
        setFormErrorMessages([]);
        setWohnsitzeHasErrors(false);
        setArbeitenHasErrors(false);
  };

  // ---- Automatische Vorbefüllung aus /api/me (Verlängerungs-Modi) --------
  // Beim Einstieg in einen Verlängerungs-Modus werden die eigenen USP-Daten
  // geladen und die Formulare damit befüllt (gleicher Weg wie das ZIP-Laden).
  // Erfolgreiche Vorbefüllung sperrt die Identitätskern-Felder (readOnly) --
  // Korrekturen laufen ausschließlich über die Vereinsverwaltung.
  useEffect(() => {
    if (appMode !== 'zuep-verlaengerung' && appMode !== 'ausweis-verlaengerung' && appMode !== 'neuantrag') {
      setApiPrefilled(false);
      return;
    }
    let cancelled = false;
    getMe(keycloak.token)
      .then((me) => {
        if (cancelled) return;
        const d: any = {
          ...me.person,
          id: me.p_id,
          adresse: me.adressen.map((a) => ({ ...a, id: a.a_id })),
          beschaeftigung: me.beschaeftigung.map((b) => ({ ...b, id: b.b_id })),
          // dokinfo (REST) nutzt dieselben art-Codes wie der Export
          // (0=Strafregister, 1=Reisepass, 2=Personalausweis) -- verifiziert
          // am Echtsystem 2026-07-06. Binärdateien gibt es hier bewusst nicht.
          dokument: me.dokinfo.map((di) => ({
            art: di.art,
            nummer: di.nummer,
            ausstellung: di.ausstellung,
          })),
        };
        applyLoadedData(d);
        setApiPrefilled(true);

        // Hinterlegtes Ausweisdokument? Dann Anzeige statt Upload -- ein bereits
        // im System liegendes Dokument wird NIE erneut erzwungen (der +10-Jahre-
        // Ablauf ist nur eine Schätzung; die echte Kosten-/Ablaufprüfung macht
        // der Freigabe-Guard server-seitig, plus die Ablauf-Erinnerungen). Bei
        // geschätztem Ablauf zeigen wir einen Hinweis, blockieren aber nicht.
        const ids = (me.dokinfo || []).filter((x) => (x.art === 1 || x.art === 2) && x.ausstellung);
        if (ids.length > 0) {
          const neuestes = ids.reduce((a, b) => (a.ausstellung > b.ausstellung ? a : b));
          const bis = moment(neuestes.ausstellung).add(10, 'years');
          setBestandsDok({
            art: neuestes.art,
            nummer: neuestes.nummer || '',
            ausstellung: neuestes.ausstellung,
            gueltigBis: bis.format('DD.MM.YYYY'),
            abgelaufen: !bis.isAfter(moment()),
          });
          setDokErsetzen(false);
        } else {
          setBestandsDok(null);
        }
      })
      .catch(() => {
        // Kein verknüpfter Datensatz / Fehler -> Formular bleibt leer,
        // ZIP-Laden steht weiterhin zur Verfügung.
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appMode]);

  const submitForm = () => {
    const f = form.current;
    if (!f) return;
    if (typeof f.requestSubmit === 'function') {
      f.requestSubmit();
    } else {
      // Ältere iOS-Safari kennen requestSubmit() nicht — der Klick verpufft
      // dort sonst kommentarlos (Vorfall Anich 10.07., Handy).
      if (f.reportValidity()) {
        f.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
      }
    }
  }

  const stateChanged = (e: ChangeEvent<HTMLElement>) => {
    resetFormValidity(e);
    const select = e.target as HTMLSelectElement;
    setState1(select.value);
    if (form.current)
      form.current.staat_2.value = '';
  }

  const handleDocArtChange = (e: ChangeEvent<HTMLElement>) => {
    resetFormValidity(e);
    const value = (e.target as HTMLSelectElement).value;
    setSelectedDocArt(value);
    if (value === '1') {
      // Reisepass: 7-9 alphanumerische Zeichen (AT: 1+7, IT: 2+7, DE: 1+8)
      setDocNummerPattern('^[A-Za-z0-9]{7,9}$');
      setDocNummerHint('7-9 Zeichen, Buchstaben und Ziffern (z.B. U1234567, AA1234567, C01X00T47)');
      setDocNummerInvalidMessage('Reisepassnummer: 7-9 Buchstaben/Ziffern (z.B. AT: U1234567, IT: AA1234567, DE: C01X00T47)');
    } else if (value === '2') {
      // Personalausweis: 6-10 alphanumerische Zeichen
      setDocNummerPattern('^[A-Za-z0-9]{6,10}$');
      setDocNummerHint('Nummer von der VORDERSEITE der Karte (z.B. PA1234567) — nicht aus der MRZ-Zeile auf der Rückseite (dort hängt eine Prüfziffer an).');
      setDocNummerInvalidMessage('Personalausweisnummer: 6-10 Buchstaben/Ziffern');
    } else if (value === '3' || value === '4' || value === '5') {
      // Fremdenpass / Konventionspass / Identitätsausweis: generisch
      setDocNummerPattern('^[A-Za-z0-9]{6,10}$');
      setDocNummerHint('Dokumentnummer wie auf dem Dokument angegeben (6-10 Zeichen).');
      setDocNummerInvalidMessage('Dokumentnummer: 6-10 Buchstaben/Ziffern');
    } else {
      setDocNummerPattern('');
      setDocNummerHint('');
      setDocNummerInvalidMessage('');
    }
    // Reset nummer validity when art changes
    if (docForm.current) {
      docForm.current.nummer.setCustomValidity('');
    }
  }

  const resetFormValidity = (e: ChangeEvent<HTMLElement>) => {
    const element = e.target as HTMLInputElement;
    if (element.value >= '0') {
      element.setCustomValidity('');
      //setValidated(false);
    }
    else {
      element.setCustomValidity('Wert auswählen');
    }
  }


  const handleModeSelect = (mode: AppMode) => {
    if (mode === 'neuantrag') {
    }
    window.history.pushState({ zuep: true }, '', '#' + mode);
    setAppMode(mode);
  };

  const modeLabels: Record<AppMode, string> = {
    'zuep-verlaengerung': 'ZÜP-Verlängerung',
    'neuantrag': 'Komplett-Antrag · ZÜP + Flughafenausweis',
    'ausweis-verlaengerung': 'Ausweis-Verlängerung · ZÜP noch gültig',
    'bg-wechsel': 'ZÜP zu uns übertragen',
    'mein-zuep': 'Mein ZÜP-Datensatz',
    'strafregister-it': 'Strafregisterauszug (Italien)',
    'dokument-nachreichen': 'Dokument nachreichen',
    'matching-review': 'Matching-Review (Admin)',
    'antraege-admin': 'Anträge (Admin)',
  };

  if (appMode === null) {
    return <ModeSelection onSelect={handleModeSelect} />;
  }

  // Read-only Ansichten (kein eigenes Formular/State im großen ZÜP-Formular unten nötig)
  if (appMode === 'mein-zuep' || appMode === 'matching-review' || appMode === 'antraege-admin' || appMode === 'bg-wechsel' || appMode === 'strafregister-it' || appMode === 'dokument-nachreichen') {
    return (
      <div className='App'>
        <header className='App-header'>
          <img src={logo} alt='Flugsportzentrum Tirol' className='App-logo' />
          <div className='App-header-text'>
            <h1>{modeLabels[appMode]}</h1>
          </div>
          <div className='ms-auto d-flex align-items-center gap-2'>
            <Button variant='outline-light' size='sm' className='mode-back-btn' onClick={goBack}>
              ← Zurück
            </Button>
            <span className='user-badge'>
              {keycloak.tokenParsed?.name || keycloak.tokenParsed?.preferred_username || ''}
            </span>
            <Button variant='outline-light' size='sm' className='mode-back-btn' onClick={() => keycloak.logout()}>
              Abmelden
            </Button>
          </div>
        </header>
        {appMode === 'mein-zuep' ? <MeinZuep /> : appMode === 'antraege-admin' ? <AntraegeAdmin /> : appMode === 'bg-wechsel' ? <BgWechsel goBack={goBack} /> : appMode === 'strafregister-it' ? <StrafregisterIt goBack={goBack} /> : appMode === 'dokument-nachreichen' ? <DokumentNachreichen goBack={goBack} /> : <MatchingReview />}
      </div>
    );
  }

  return (
    <div className='App'>
      <header className='App-header'>
        <img src={logo} alt='Flugsportzentrum Tirol' className='App-logo' />
        <div className='App-header-text'>
          <h1>{appMode === 'ausweis-verlaengerung' ? 'Flughafenausweis-Verlängerung' : 'ZÜP Eingabeformular'}</h1>
          <h3>{modeLabels[appMode]}</h3>
        </div>
        <div className='ms-auto d-flex align-items-center gap-2'>
          <Button variant='outline-light' size='sm' className='mode-back-btn' onClick={goBack}>
            ← Zurück
          </Button>
          <span className='user-badge'>
            {keycloak.tokenParsed?.name || keycloak.tokenParsed?.preferred_username || ''}
          </span>
          <Button variant='outline-light' size='sm' className='mode-back-btn' onClick={() => keycloak.logout()}>
            Abmelden
          </Button>
        </div>
      </header>
      <Container fluid='md' className='py-4'>
        <div className='load-bar'>
          <input type='file' accept='.json,.zip' ref={loadFileInput} style={{ display: 'none' }} onChange={loadJson} />
          <Button variant='outline-secondary' onClick={() => loadFileInput.current?.click()}>
            Vorjahresdaten laden
          </Button>
          <span className='load-hint'>
            Optional: Die Felder werden bereits automatisch aus der ZÜP-Datenbank vorbefüllt.
            Alternativ kann eine ZIP-Datei vom Vorjahr geladen werden.
          </span>
        </div>

        {/* Ausweis-Verlängerung: Full form for PDF data */}
        {appMode === 'ausweis-verlaengerung' && <>
          <div className='required-legend'>{requiredIcon} = Pflichtfelder</div>
          <Alert variant='warning' className='mb-3'>
            <strong>Hinweis:</strong> Die erzeugten PDFs (ANTRAG &amp; ANLAGE 4B) müssen nach dem Download <strong>unterschrieben</strong> werden, bevor sie an den Flughafen gesendet werden.
          </Alert>
          <div className='form-section'>
            <h5 className='mb-3'>Persönliche Daten</h5>
            {apiPrefilled && (
              <Alert variant='info' className='py-2'>
                🔒 Deine Identitätsdaten wurden aus der ZÜP-Datenbank übernommen und sind hier
                nicht änderbar. Stimmt etwas nicht?{' '}
                <Alert.Link href='mailto:info@dr-moser.tirol?subject=ZÜP-Datenkorrektur'>
                  Bitte kurz an die Vereinsverwaltung melden.
                </Alert.Link>
              </Alert>
            )}
            <Row className={rowClass}>
              <Col md={5} xs={12}>
                <Form.Group className={groupClass}>
                  <Form.Label>Familienname{requiredIcon}</Form.Label>
                  <Form.Control type='text' readOnly={apiPrefilled} value={ausweisFamname} onChange={(e) => setAusweisFamname(e.target.value)} />
                </Form.Group>
              </Col>
              <Col md={5} xs={12}>
                <Form.Group className={groupClass}>
                  <Form.Label>Vorname{requiredIcon}</Form.Label>
                  <Form.Control type='text' readOnly={apiPrefilled} value={ausweisVorname} onChange={(e) => setAusweisVorname(e.target.value)} />
                </Form.Group>
              </Col>
              <Col md={2} xs={6}>
                <Form.Group className={groupClass}>
                  <Form.Label>Geschlecht{requiredIcon}</Form.Label>
                  <Form.Select disabled={apiPrefilled} value={ausweisGeschlecht} onChange={(e) => setAusweisGeschlecht(e.target.value)}>
                    <option value='-1'>Wählen</option>
                    <option value='0'>männlich</option>
                    <option value='1'>weiblich</option>
                    <option value='2'>divers</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Row className={rowClass}>
              <Col md={4} xs={12}>
                <Form.Group className={groupClass}>
                  <Form.Label>Geburtsdatum{requiredIcon}</Form.Label>
                  <Form.Control type='date' readOnly={apiPrefilled} value={ausweisGebdatum} onChange={(e) => setAusweisGebdatum(e.target.value)} max={moment().add(-16, 'years').format(JSON_DATE_FORMAT)} />
                </Form.Group>
              </Col>
              <Col md={4} xs={12}>
                <Form.Group className={groupClass}>
                  <Form.Label>Staatsangehörigkeit{requiredIcon}</Form.Label>
                  <div><Form.Text className="text-muted">Muss die Staatsangehörigkeit des hochgeladenen Ausweisdokuments sein.</Form.Text></div>
                  <Form.Select disabled={apiPrefilled} value={ausweisNationalitaet} onChange={(e) => setAusweisNationalitaet(e.target.value)}>
                    <option value=''>Bitte wählen</option>
                    {Object.keys(StatesAlpha2List.ALPHA2CODES).map((key) =>
                      <option key={key} value={key}>{StatesAlpha2List.ALPHA2CODES[key]}</option>
                    )}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4} xs={12}>
                <Form.Group className={groupClass}>
                  <Form.Label>Vorgesehene Tätigkeit</Form.Label>
                  <Form.Control type='text' value={ausweisTaetigkeit} onChange={(e) => setAusweisTaetigkeit(e.target.value)} placeholder='z.B. Pilot' />
                </Form.Group>
              </Col>
            </Row>
            <Row className={rowClass}>
              <Col md={4} xs={6}>
                <Form.Group className={groupClass}>
                  <Form.Label>Geburtsort PLZ</Form.Label>
                  <Form.Control type='text' readOnly={apiPrefilled} value={ausweisGebOrtPlz} onChange={(e) => setAusweisGebOrtPlz(e.target.value)} />
                </Form.Group>
              </Col>
              <Col md={4} xs={6}>
                <Form.Group className={groupClass}>
                  <Form.Label>Geburtsort</Form.Label>
                  <Form.Control type='text' readOnly={apiPrefilled} value={ausweisGebOrt} onChange={(e) => setAusweisGebOrt(e.target.value)} />
                </Form.Group>
              </Col>
              <Col md={4} xs={12}>
                <Form.Group className={groupClass}>
                  <Form.Label>Geburtsland</Form.Label>
                  <Form.Select disabled={apiPrefilled} value={ausweisGebOrtLand} onChange={(e) => setAusweisGebOrtLand(e.target.value)}>
                    <option value=''>Bitte wählen</option>
                    {Object.keys(StatesAlpha2List.ALPHA2CODES).map((key) =>
                      <option key={key} value={key}>{StatesAlpha2List.ALPHA2CODES[key]}</option>
                    )}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </div>
          <div className='form-section'>
            <h5 className='mb-3'>Kontakt &amp; Wohnadresse</h5>
            <Row className={rowClass}>
              <Col md={6} xs={12}>
                <Form.Group className={groupClass}>
                  <Form.Label>Telefon</Form.Label>
                  <Form.Control type='text' value={ausweisTel} onChange={(e) => setAusweisTel(e.target.value)} placeholder='+43 ...' />
                </Form.Group>
              </Col>
              <Col md={6} xs={12}>
                <Form.Group className={groupClass}>
                  <Form.Label>E-Mail{requiredIcon}</Form.Label>
                  <Form.Control type='email' value={ausweisMail} onChange={(e) => setAusweisMail(e.target.value)} />
                </Form.Group>
              </Col>
            </Row>
            <Row className={rowClass}>
              <Col md={2} xs={4}>
                <Form.Group className={groupClass}>
                  <Form.Label>PLZ</Form.Label>
                  <Form.Control type='text' value={ausweisPlz} onChange={(e) => setAusweisPlz(e.target.value)} />
                </Form.Group>
              </Col>
              <Col md={4} xs={8}>
                <Form.Group className={groupClass}>
                  <Form.Label>Wohnort</Form.Label>
                  <Form.Control type='text' value={ausweisOrt} onChange={(e) => setAusweisOrt(e.target.value)} />
                </Form.Group>
              </Col>
              <Col md={6} xs={12}>
                <Form.Group className={groupClass}>
                  <Form.Label>Straße &amp; Hausnummer</Form.Label>
                  <Form.Control type='text' value={ausweisStrasse} onChange={(e) => setAusweisStrasse(e.target.value)} />
                </Form.Group>
              </Col>
            </Row>
          </div>
        </>}

        {showZuepSections && <div className='required-legend'>{requiredIcon} = Pflichtfelder</div>}
        {showZuepSections && <Form ref={form} noValidate validated={validated} onSubmit={handleSubmit}>
          {apiPrefilled && (
            <Alert variant='info' className='py-2'>
              🔒 Deine Identitätsdaten wurden aus der ZÜP-Datenbank übernommen und sind hier
              nicht änderbar (sie müssen exakt deinem Ausweisdokument entsprechen).
              Stimmt etwas nicht?{' '}
              <Alert.Link href='mailto:info@dr-moser.tirol?subject=ZÜP-Datenkorrektur'>
                Bitte kurz an die Vereinsverwaltung melden.
              </Alert.Link>{' '}
              Wohnsitze, Beschäftigungen und Kontaktdaten kannst du normal ergänzen.
            </Alert>
          )}
          <Row className={rowClass}>
            <Col md={2} xs={12}>
              <Form.Group className={groupClass}>
                <Form.Label>Titel</Form.Label>
                <Form.Control name='titel' type='text'></Form.Control>
                <Form.Text className="text-muted">
                  Falls vorhanden.
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={5} xs={12}>
              <Form.Group className={groupClass}>
                <Form.Label>Familienname{requiredIcon}</Form.Label>
                <Form.Control name='famname' readOnly={apiPrefilled} type='text' required></Form.Control>
                <Form.Text className="text-muted">
                  Der Familienname vom Ausweisdokument.
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={5} xs={12}>
              <Form.Group className={groupClass}>
                <Form.Label>Vorname{requiredIcon}</Form.Label>
                <Form.Control name='vorname' readOnly={apiPrefilled} type='text' required pattern="^\S*$"></Form.Control>
                <Form.Control.Feedback type='invalid' >
                  Vorname muss angegeben werden und darf keine Leerzeichen enthalten
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                  Nur der erste zusammenhängende Vorname vom Ausweisdokument.
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>
          <Row className={rowClass}>
            <Col md={6} xs={12}>
              <Form.Group className={groupClass}>
                <Form.Label>Geschlecht{requiredIcon}</Form.Label>
                <Form.Select name='geschlecht' disabled={apiPrefilled} required onChange={resetFormValidity}>
                  <option value='-1'>Geschlecht auswählen</option>
                  <option value='0'>männlich</option>
                  <option value='1'>weiblich</option>
                  <option value='2'>divers</option>
                </Form.Select>
                <Form.Control.Feedback type='invalid' >
                  {geschlechtInvalidMessage}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6} xs={12}>
              <Form.Group className={groupClass}>
                <Form.Label>Geb.Datum{requiredIcon}</Form.Label>
                <Form.Control name='gebdatum' readOnly={apiPrefilled} type='date' max={moment().add(-16, 'years').format(JSON_DATE_FORMAT)} required></Form.Control>
              </Form.Group>
            </Col>
          </Row>
          <Row className={rowClass}>
            <Col md={6} xs={12}>
              <Form.Group>
                <Form.Label>Geb.Name</Form.Label>
                <Form.Control name='gebname' readOnly={apiPrefilled} type='text' placeholder='nur der Nachname, z. B. Huber'></Form.Control>
                <Form.Text className="text-muted">
                  Nur wenn abweichend vom Familiennamen (z.B. durch Heirat oder Adoption) — nur der Nachname laut Geburtsurkunde, ohne Vornamen. Sonst leer lassen.
                </Form.Text>
                <Form.Control.Feedback type='invalid'>Nur der Nachname bei der Geburt, ohne Vornamen.</Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6} xs={12}>
              <Form.Group>
                <Form.Label>Geb.Ort{requiredIcon}</Form.Label>
                <Form.Control name='geb_ort' readOnly={apiPrefilled} type='text' required></Form.Control>
              </Form.Group>
            </Col>
          </Row>
          <Row className={rowClass}>
            <Col md={6} xs={12}>
              <Form.Group>
                <Form.Label>Geb.Ort PLZ</Form.Label>
                {/* Schnittstelle: max. 12 Zeichen, sonst USP 400 (Fall Sauer 21.09.2026: "Dresden 01257").
                    Kein Ortsname: eine PLZ hat nie drei Buchstaben hintereinander. */}
                <Form.Control name='geb_ort_plz' readOnly={apiPrefilled} type='text' maxLength={12}
                  pattern="^(?!.*[A-Za-zÄÖÜäöüß]{3})[A-Za-z0-9ÄÖÜäöüß -]{1,12}$"
                  placeholder='nur die Postleitzahl, z. B. 6020'></Form.Control>
                <Form.Control.Feedback type='invalid'>Nur die Postleitzahl (max. 12 Zeichen) – der Ort gehört ins Feld „Geb.Ort".</Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6} xs={12}>
              <Form.Group>
                <Form.Label>Geb.Ort Land{requiredIcon}</Form.Label>
                <Form.Select name='geb_ort_land' disabled={apiPrefilled} required onChange={resetFormValidity}>
                  <option value={''}>Bitte Land wählen</option>
                  {
                    Object.keys(StatesAlpha2List.ALPHA2CODES).map((key, index) =>
                      <option value={key}>{StatesAlpha2List.ALPHA2CODES[key]}</option>
                    )
                  }
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          <Row className={rowClass}>
            <Col md={6} xs={12}>
              <Form.Group>
                <Form.Label>Vorname Mutter{requiredIcon}</Form.Label>
                <Form.Control name='vorname_m' readOnly={apiPrefilled} type='text' required pattern="^\S*$"></Form.Control>
                <Form.Control.Feedback type='invalid' >
                  Vorname muss angegeben werden und darf keine Leerzeichen enthalten
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6} xs={12}>
              <Form.Group>
                <Form.Label>Vorname Vater{requiredIcon}</Form.Label>
                <Form.Control name='vorname_v' readOnly={apiPrefilled} type='text' required pattern="^\S*$"></Form.Control>
                <Form.Control.Feedback type='invalid' >
                  Vorname muss angegeben werden und darf keine Leerzeichen enthalten
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
          <Row className={rowClass}>
            <Col md={6} xs={12}>
              <Form.Group>
                <Form.Label>Staatsangehörigkeit 1{requiredIcon}</Form.Label>
                <Form.Select name='staat_1' disabled={apiPrefilled} required onChange={stateChanged}>
                  <option value={''}>Bitte Staatsangehörigkeit wählen</option>
                  {
                    Object.keys(StatesAlpha2List.ALPHA2CODES).map((key, index) =>
                      <option value={key}>{StatesAlpha2List.ALPHA2CODES[key]}</option>
                    )
                  }
                </Form.Select>
                <Form.Control.Feedback type='invalid' >
                  {staat1Invalidity}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6} xs={12}>
              <Form.Group>
                <Form.Label>Staatsangehörigkeit 2</Form.Label>
                <Form.Select name='staat_2' disabled={apiPrefilled} >
                  <option value={''}>Staatsangehörigkeit wählen (falls zutreffend)</option>
                  {
                    Object.keys(StatesAlpha2List.ALPHA2CODES).filter((code) => { return state1 !== code }).map((key, index) =>
                      <option value={key}>{StatesAlpha2List.ALPHA2CODES[key]}</option>
                    )
                  }
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          <Row className={rowClass}>
            <Col md={6} xs={12}>
              <Form.Group>
                <Form.Label>Kontakt-Telefon{requiredIcon}</Form.Label>
                <Form.Control name='kontakt_tel' type='text' required></Form.Control>
              </Form.Group>
            </Col>
            <Col md={6} xs={12}>
              <Form.Group>
                <Form.Label>Kontakt-Mail{requiredIcon}</Form.Label>
                <Form.Control name='kontakt_mail' type='email' required></Form.Control>
              </Form.Group>
            </Col>
          </Row>
        </Form>}
        {showZuepSections && <Row>
          <Accordion defaultActiveKey={['0', '1', '2', '3']} className='py-4' alwaysOpen>
            <Accordion.Item eventKey='0'>
              <Accordion.Header>{docFormValidated && docForm.current?.reportValidity() === false && <FaTimesCircle style={{ paddingRight: '1em' }} color='red' size={40} />}Identitätsdokument</Accordion.Header>
              <Accordion.Body >
                <Form ref={docForm} noValidate validated={docFormValidated}>
                  <Container className='py-4'>
                    <Row className={rowClass}>
                      <Col md={4} xs={12}>
                        <Form.Group>
                          <Form.Label>Dokumentart{requiredIcon}</Form.Label>
                          <Form.Select required name='art' disabled={!!bestandsDok && !dokErsetzen} onChange={handleDocArtChange} value={selectedDocArt}>
                            <option value='-1'>Bitte Dokumentart auswählen</option>
                            <option value='1'>Reisepass</option>
                            <option value='2'>Personalausweis</option>
                            <option value='3'>Fremdenpass</option>
                            <option value='4'>Konventionspass</option>
                            <option value='5'>Identitätsausweis</option>
                          </Form.Select>
                          <Form.Control.Feedback type='invalid' >
                            {docArtInvalidMessage}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={4} xs={12}>
                        <Form.Group>
                          <Form.Label>Dokumentnummer{requiredIcon}</Form.Label>
                          <Form.Control type='text' name='nummer' readOnly={!!bestandsDok && !dokErsetzen} required pattern={docNummerPattern || undefined}></Form.Control>
                          <Form.Control.Feedback type='invalid'>
                            {docNummerInvalidMessage || 'Bitte Dokumentnummer angeben'}
                          </Form.Control.Feedback>
                          {docNummerHint && <Form.Text className="text-muted">{docNummerHint}</Form.Text>}
                        </Form.Group>
                      </Col>
                      <Col md={4} xs={12}>
                        <Form.Group>
                          <Form.Label>Austellungsdatum{requiredIcon}</Form.Label>
                          <Form.Control required readOnly={!!bestandsDok && !dokErsetzen} type='date' name='ausstellung' max={moment().format('YYYY-MM-DD')}></Form.Control>
                        </Form.Group>
                      </Col>
                      <Col md={4} xs={12}>
                        <Form.Group>
                          {/* Das echte Ablaufdatum steht auf dem Dokument ("Gültig bis"
                              / "Valid until"). Es abzufragen beendet das Schätzen: aus
                              dem Ausstellungsdatum lässt es sich NICHT zuverlässig
                              ableiten, weil die Laufzeit vom Land und vom Alter bei
                              Ausstellung abhängt (Fall Schafferer, 18.08.2026 — mit 13
                              ausgestellt, 10 Jahre gültig, von der Heuristik als
                              abgelaufen geführt). */}
                          <Form.Label>Gültig bis{requiredIcon}</Form.Label>
                          <Form.Control required readOnly={!!bestandsDok && !dokErsetzen} type='date' name='gueltig_bis'></Form.Control>
                          <Form.Control.Feedback type='invalid'>
                            Bitte das Ablaufdatum eintragen
                          </Form.Control.Feedback>
                          <Form.Text className='text-muted'>
                            Steht auf dem Dokument unter „Gültig bis / Valid until".
                          </Form.Text>
                        </Form.Group>
                      </Col>
                    </Row>
                    {bestandsDok && !dokErsetzen && (
                      <Row className={rowClass}>
                        <Col xs={12}>
                          {bestandsDok.abgelaufen ? (
                            <Alert variant='warning' className='mb-2'>
                              ⚠️ Dein {bestandsDok.art === 1 ? 'Reisepass' : 'Personalausweis'}
                              {bestandsDok.nummer ? ` (Nr. •••${bestandsDok.nummer.slice(-3)})` : ''} ist hinterlegt, dürfte aber
                              inzwischen abgelaufen sein (ausgestellt am {moment(bestandsDok.ausstellung).format('DD.MM.YYYY')},
                              geschätzt gültig bis {bestandsDok.gueltigBis}).{' '}
                              <strong>Falls du bereits einen neuen Ausweis hast, lade ihn bitte hoch</strong> — sonst kannst du
                              einfach fortfahren.
                              <div className='mt-2'>
                                <Button variant='outline-secondary' size='sm' onClick={() => setDokErsetzen(true)}>
                                  Neues Dokument hochladen
                                </Button>
                              </div>
                            </Alert>
                          ) : (
                            <Alert variant='success' className='mb-2'>
                              ✅ Dein {bestandsDok.art === 1 ? 'Reisepass' : 'Personalausweis'}
                              {bestandsDok.nummer ? ` (Nr. •••${bestandsDok.nummer.slice(-3)})` : ''} ist bereits hinterlegt —
                              ausgestellt am {moment(bestandsDok.ausstellung).format('DD.MM.YYYY')}, gültig (geschätzt) bis {bestandsDok.gueltigBis}.
                              <strong> Kein neuer Upload nötig.</strong>{' '}
                              <Button variant='outline-secondary' size='sm' className='ms-2' onClick={() => setDokErsetzen(true)}>
                                Neues/anderes Dokument hochladen
                              </Button>
                            </Alert>
                          )}
                        </Col>
                      </Row>
                    )}
                    {(selectedDocArt === '2' || docExpiredWarning) && !(bestandsDok && !dokErsetzen) && <Row className={rowClass}>
                      {selectedDocArt === '2' && <Col xs={12}><Alert variant='info'>Bei einem Personalausweis muss Vorder- und Rückseite hochgeladen werden</Alert></Col>}
                      {docExpiredWarning && <Col xs={12}><Alert variant='danger'>{docExpiredWarning}</Alert></Col>}
                    </Row>}
                    {!(bestandsDok && !dokErsetzen) && <Row className={rowClass}>
                      <Col xs={12}>
                        <Form.Group>
                          <Form.Label>Dokument-Datei{!loadedDocName && requiredIcon}</Form.Label>
                          {/* jpeg, jpg, jpe, png, gif, pdf*/}
                          <Form.Control type='file' name='file' accept='image/jpeg,image/png,image/gif,application/pdf' required={!loadedDocName && !(bestandsDok && !dokErsetzen)} onChange={(e: ChangeEvent<HTMLInputElement>) => checkFile(e, setFileInvalidMessage, setFileKomprimiertInfo)}></Form.Control>
                          <Form.Control.Feedback type='invalid' >
                            {fileInvalidMessage}
                          </Form.Control.Feedback>
                          {fileKomprimiertInfo && <Form.Text className="text-success">✅ {fileKomprimiertInfo}</Form.Text>}
                          {loadedDocName && <Form.Text className="text-success">Vorjahr:{loadedDocName}</Form.Text>}
                        </Form.Group>
                      </Col>
                    </Row>}
                    {selectedDocArt === '2' && !(bestandsDok && !dokErsetzen) && <Row className={rowClass}>
                      <Col xs={12}>
                        <Form.Group>
                          <Form.Label>Dokument-Datei (Rückseite)</Form.Label>
                          {/* jpeg, jpg, jpe, png, gif, pdf*/}
                          <Form.Control type='file' name='file2' accept='image/jpeg,image/png,image/gif,application/pdf' onChange={(e: ChangeEvent<HTMLInputElement>) => checkFile(e, setFile2InvalidMessage, setFile2KomprimiertInfo)}></Form.Control>
                          <Form.Control.Feedback type='invalid' >
                            {file2InvalidMessage}
                          </Form.Control.Feedback>
                          {file2KomprimiertInfo && <Form.Text className="text-success">✅ {file2KomprimiertInfo}</Form.Text>}
                          {loadedDoc2Name && <Form.Text className="text-success">Vorjahr:{loadedDoc2Name}</Form.Text>}
                        </Form.Group>
                      </Col>
                    </Row>}
                  </Container>
                </Form>
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey='1'>
              <Accordion.Header>{wohnsitzeHasErrors && <FaTimesCircle style={{ paddingRight: '1em' }} color='red' size={40} />}Wohnsitze</Accordion.Header>
              <Accordion.Body >
                <Container className='py-3'>
                  {wohnsitze.length > 0 &&
                    <Container>
                      <Row className='text-start fw-bold border-bottom pb-2 mb-1 d-none d-md-flex'>
                        <Col md={1}>Land</Col>
                        <Col md={1} className={Constants.HIDDEN_COLUMN_CLASSES}>PLZ</Col>
                        <Col md={2}>Ort</Col>
                        <Col md={2}>Strasse</Col>
                        <Col md={1} className={Constants.HIDDEN_COLUMN_CLASSES}>Hausnr.</Col>
                        <Col md={2}>Von</Col>
                        <Col md={2} className={Constants.HIDDEN_COLUMN_CLASSES}>Bis</Col>
                        <Col md={1}></Col>
                      </Row>
                      {wohnsitze.map((wohnsitz, index) => (
                        <WohnsitzZeile wohnsitz={wohnsitz} index={index} deleteCallback={deleteWohnsitz} editCallback={editWohnsitz} key={index}></WohnsitzZeile>
                      ))
                      }
                    </Container>
                  }
                  <LueckenAnzeige eintraege={wohnsitze} was='Wohnsitze' />
                  {!showWohnsitzAdd && wohnsitze.length !== 0 && <Row className='mt-3'><Col className='text-end'>
                    <Button onClick={() => setShowWohnsitzAdd(!showWohnsitzAdd)}><FaPlus ></FaPlus> Wohnsitz hinzufügen</Button>
                  </Col></Row>}
                  {(wohnsitze.length === 0 || showWohnsitzAdd) && <AddWohnsitzComponent callbackOnAdd={wohnsitzAdd} callbackAbort={() => setShowWohnsitzAdd(false)}></AddWohnsitzComponent>}
                </Container>
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey='2'>
              <Accordion.Header>{arbeitenHasErrors && <FaTimesCircle style={{ paddingRight: '1em' }} color='red' size={40} />}Beschäftigungsverhältnisse, Aus- und Weiterbildungen</Accordion.Header>
              <Accordion.Body >
                <Container className='py-3'>
                  {arbeiten.length > 0 &&
                    <Container>
                      <Row className='text-start fw-bold border-bottom pb-2 mb-1 d-none d-md-flex'>
                        <Col md={2} className={Constants.HIDDEN_COLUMN_CLASSES}>Art</Col>
                        <Col md={2}>Organisation</Col>
                        <Col md={2} className={Constants.HIDDEN_COLUMN_CLASSES}>Tätigkeit</Col>
                        <Col md={1} className={Constants.HIDDEN_COLUMN_CLASSES}>Land</Col>
                        <Col md={2}>Von</Col>
                        <Col md={2} className={Constants.HIDDEN_COLUMN_CLASSES}>Bis</Col>
                        <Col md={1}></Col>
                      </Row>
                      {arbeiten.map((arbeit, index) => (
                        <ArbeitsZeile arbeit={arbeit} key={'a' + index} index={index} deleteCallback={deleteArbeit} editCallback={editArbeit}></ArbeitsZeile>
                      ))
                      }
                    </Container>
                  }
                  <LueckenAnzeige eintraege={arbeiten} was='Beschäftigungen' />
                  {!showArbeitAdd && arbeiten.length !== 0 && <Row className='mt-3'><Col className='text-end'>
                    <Button onClick={() => setShowArbeitAdd(!showArbeitAdd)}><FaPlus ></FaPlus> Beschäftigung hinzufügen</Button>
                  </Col></Row>}
                  {(arbeiten.length === 0 || showArbeitAdd) && <AddArbeitComponent callbackOnAdd={arbeitAdd} callbackAbort={() => setShowArbeitAdd(false)}></AddArbeitComponent>}
                </Container>
              </Accordion.Body>
            </Accordion.Item>
            {(() => {
              // Behörden-Regel (Bartek, 31.08.2026): Strafregisterauszüge sind
              // WOHNSITZ-basiert. Ein Land, in dem nur gearbeitet wurde
              // (Grenzpendler, Fall Locher 12.09.2026), löst KEINE Pflicht aus —
              // die Kachel erscheint dort nur als freiwilliges Angebot.
              const wohnsitzLaender = [...new Set(wohnsitze.filter(w => w.land !== 'AT').map(w => w.land))];
              const beschaeftigungsLaender = [...new Set(arbeiten.filter(a => a.land && a.land !== 'AT').map(a => a.land))]
                .filter(l => !wohnsitzLaender.includes(l));
              const auslandsLaender = [...wohnsitzLaender, ...beschaeftigungsLaender];
              return auslandsLaender.length > 0 && (
                <Accordion.Item eventKey='3'>
                  <Accordion.Header>Strafregisterauszüge</Accordion.Header>
                  <Accordion.Body>
                    <Form ref={strafregisterForm} noValidate validated={validated}>
                      <Container className='py-3'>
                        {auslandsLaender.map(land =>
                          <StrafregisterInput land={land} key={land} wohnsitze={wohnsitze.filter(w => w.land === land)} nurBeschaeftigung={beschaeftigungsLaender.includes(land)} hasLoadedFile={loadedStrafregister.some(sr => sr.land === land && !!sr.name)} loadedFileName={loadedStrafregister.find(sr => sr.land === land)?.name} />
                        )}
                      </Container>
                    </Form>
                  </Accordion.Body>
                </Accordion.Item>
              );
            })()}
          </Accordion>
        </Row>}

        {/* ANTRAG & ANLAGE 4B Section — visible in neuantrag and ausweis-verlaengerung */}
        {showPdfSection && <Row className='py-3'>
          <Col xs={12}>
            <h5>ANTRAG &amp; ANLAGE 4B (Sicherheitsschulung)</h5>
            {appMode === 'neuantrag' && (
              <Alert variant='info' className='mb-3'>
                <small>Für deinen Flughafenausweis gehören zum Antrag die Formulare <strong>ANTRAG</strong> und <strong>ANLAGE 4B</strong> (Sicherheitsschulung). Wir befüllen beide automatisch mit deinen Angaben — du musst nur unten <strong>unterschreiben</strong>, den Rest übernehmen wir: Nach dem Einreichen gehen die Formulare direkt an die Ausweisstelle des Flughafens (du bekommst eine Kopie per E-Mail).</small>
              </Alert>
            )}
          </Col>
        </Row>}
        {showPdfSection && <Row className='py-3'>
          <h5>Rechnungsempfänger</h5>
          <Col md={4} xs={12}>
            <Form.Group className={groupClass}>
              <Form.Label>Firma / Name</Form.Label>
              <Form.Control type='text' value={rechnungFirma} onChange={(e) => setRechnungFirma(e.target.value)} placeholder='Firma oder Name' />
            </Form.Group>
          </Col>
          <Col md={4} xs={12}>
            <Form.Group className={groupClass}>
              <Form.Label>Straße</Form.Label>
              <Form.Control type='text' value={rechnungStrasse} onChange={(e) => setRechnungStrasse(e.target.value)} placeholder='Straße und Hausnummer' />
            </Form.Group>
          </Col>
          <Col md={4} xs={12}>
            <Form.Group className={groupClass}>
              <Form.Label>PLZ / Ort</Form.Label>
              <Form.Control type='text' value={rechnungPlzort} onChange={(e) => setRechnungPlzort(e.target.value)} placeholder='PLZ und Ort' />
            </Form.Group>
          </Col>
        </Row>}

        {/* Error summary */}
        {formErrorMessages.length > 0 &&
          <div ref={errorSummaryRef} className='error-summary'>
            <Alert variant='danger' className='mb-0'>
              <Alert.Heading className='error-summary-heading'>Bitte folgende Fehler beheben:</Alert.Heading>
              <ul className='error-summary-list'>
                {formErrorMessages.map((message, i) => <li key={i}>{message}</li>)}
              </ul>
            </Alert>
          </div>
        }

        {showPdfSection && (
          <Row className='mb-3'>
            <Col>
              <h5>Unterschrift{appMode === 'neuantrag' && <FaAsterisk style={{ color: 'red', paddingLeft: '4px', fontSize: '0.6em', verticalAlign: 'super' }} />}</h5>
              <p className='text-muted mb-2' style={{ fontSize: '0.85rem' }}>
                {appMode === 'neuantrag'
                  ? 'Deine Unterschrift kommt auf die Flughafenausweis-Formulare (ANTRAG + ANLAGE 4B) — ohne sie kann der Antrag nicht abgeschickt werden.'
                  : 'Deine Unterschrift wird in den Antrag übernommen — die fertigen PDFs kannst du danach mit einem Klick direkt an die Ausweisstelle senden.'}
              </p>
              <SignaturePad onChange={setUnterschriftPng} />
            </Col>
          </Row>
        )}

        {/* Submit button */}
        <Row className='submit-row'>
          <Col className='text-end'>
            {appMode === 'ausweis-verlaengerung' ? (
              <Button onClick={handleSubmitAusweisOnly} variant="success" className="btn-lg">
                PDFs generieren
              </Button>
            ) : (
              <Button onClick={submitForm} variant="success" className="btn-lg">
                Daten validieren und abschicken
              </Button>
            )}
          </Col>
        </Row>
      </Container>

      {/* Download modal */}
      {jsonUrl && <Modal show={showDialog} onHide={() => setShowDialog(false)}>
        <Modal.Header closeButton>
          <Modal.Body>
            {appMode === 'ausweis-verlaengerung'
              ? 'Fertig — deine Unterschrift ist drauf. Jetzt mit einem Klick an die Ausweisstelle senden.'
              : 'Fast geschafft! Datenpaket generieren und per Mail verschicken.'}
          </Modal.Body>
        </Modal.Header>
        <Modal.Body>
          {appMode === 'ausweis-verlaengerung' ? (
            <>
              {ausweisSendeErgebnis ? (
                <Alert variant='success' className='mb-3'>✅ {ausweisSendeErgebnis}</Alert>
              ) : (
                <div className='border rounded p-3 mb-3' style={{ background: '#f8f9fa' }}>
                  <p className='mb-1'><strong>✅ Empfohlen: Direkt an die Ausweisstelle senden</strong></p>
                  <p className='text-muted mb-2' style={{ fontSize: '0.85rem' }}>
                    Antrag und Schulungsanmeldung sind fertig und tragen deine Unterschrift —
                    mit einem Klick gehen sie an security@innsbruck-airport.com, du bekommst eine Kopie per E-Mail.
                  </p>
                  {ausweisSendeFehler && <Alert variant='danger' className='py-2'>{ausweisSendeFehler}</Alert>}
                  <Button variant='success' disabled={ausweisSendet} onClick={handleAusweisSenden}>
                    {ausweisSendet ? 'Wird gesendet …' : '📨 Jetzt an die Ausweisstelle senden'}
                  </Button>
                </div>
              )}
              <p className='text-muted mb-1' style={{ fontSize: '0.85rem' }}>Oder lieber selbst versenden:</p>
              <p>1. <a href={jsonUrl} download={sanitizeFilename('flughafenausweis_' + new Date().toISOString().split('T')[0] + '_' + ((json as any)?.famname || '') + '_' + ((json as any)?.vorname || '')) + '.zip'}>ZIP mit PDFs herunterladen</a> (Unterschrift ist schon drauf)</p>
              <p>2. An <a href={'mailto:security@innsbruck-airport.com?subject=Flughafenausweis ' + ((json as any)?.vorname || '') + ' ' + ((json as any)?.famname || '')}>security@innsbruck-airport.com</a> senden</p>
            </>
          ) : (
            <>
              {viewAs && (
                <Alert variant='warning' className='mb-3'>
                  👁 <strong>Ansehen-als-Modus (Mitglied {viewAs}).</strong> Diese Ansicht ist nur lesend.
                  Ein Einreichen würde den Antrag <strong>deinem</strong> Admin-Konto zuordnen, nicht dem
                  Mitglied. Zum echten Einreichen muss sich das Mitglied selbst einloggen.
                </Alert>
              )}
              {!viewAs && <div className='border rounded p-3 mb-3' style={{ background: '#f8f9fa' }}>
                <p className='mb-1'><strong>✅ Empfohlen: Direkt über das Portal einreichen</strong></p>
                <p className='text-muted mb-2' style={{ fontSize: '0.85rem' }}>
                  Dein Antrag geht damit sofort an die Vereinsverwaltung — kein E-Mail-Versand nötig.
                  Wir prüfen die Angaben und leiten dann die behördliche Überprüfung ein.
                </p>
                <p className='text-muted mb-2' style={{ fontSize: '0.85rem' }}>
                  💶 <strong>Kosten:</strong> Für die Bearbeitung fallen Gebühren laut{' '}
                  <a href='https://www.innsbruck-airport.com/business-aviation/aviation/entgeltordnung-zfbb' target='_blank' rel='noreferrer'>
                    Entgeltordnung des Flughafens Innsbruck
                  </a>{' '}
                  an; die Abrechnung läuft über den Verein.
                </p>
                <Form.Check
                  type='checkbox'
                  id='einreich-richtig'
                  className='mb-1'
                  label='Meine Angaben sind vollständig und richtig. Mir ist bewusst, dass falsche Angaben zur Ablehnung durch die Behörde und zu zusätzlichen Kosten führen können.'
                  checked={einreichRichtig}
                  onChange={(e) => setEinreichRichtig(e.target.checked)}
                  disabled={einreichBusy || einreichErgebnis?.ok === true}
                />
                <Form.Check
                  type='checkbox'
                  id='einreich-kosten'
                  className='mb-2'
                  label='Mir ist bewusst, dass mit der Bearbeitung meines Antrags Kosten ausgelöst werden.'
                  checked={einreichKosten}
                  onChange={(e) => setEinreichKosten(e.target.checked)}
                  disabled={einreichBusy || einreichErgebnis?.ok === true}
                />
                <Button
                  variant='danger'
                  disabled={!einreichRichtig || !einreichKosten || !antragBlob || einreichBusy || einreichErgebnis?.ok === true}
                  onClick={async () => {
                    if (!antragBlob) return;
                    setEinreichBusy(true);
                    setEinreichErgebnis(null);
                    try {
                      const filename = sanitizeFilename('zuep_' + new Date().toISOString().split('T')[0] + '_' + ((json as any)?.famname || '') + '_' + ((json as any)?.vorname || '')) + '.zip';
                      await submitAntrag(
                        keycloak.token,
                        antragBlob,
                        appMode === 'neuantrag' ? 'erstantrag' : 'verlaengerung',
                        filename,
                        { richtig: einreichRichtig, kosten: einreichKosten },
                      );
                      setEinreichErgebnis({ ok: true, text: 'Antrag verbindlich eingereicht! Wir prüfen deine Angaben und melden uns, sobald es weitergeht. Du musst nichts weiter tun.' });
                      // Erstantrag: signierte Flughafen-Formulare direkt an die
                      // Ausweisstelle — ohne sie übernimmt sie den Fall nicht.
                      if (appMode === 'neuantrag' && letzteAusweisDaten) void handleAusweisSenden();
                    } catch (err) {
                      const detail = err instanceof ApiError && err.status === 409
                        ? 'Es liegt bereits ein eingereichter Antrag von dir zur Prüfung vor.'
                        : 'Einreichung fehlgeschlagen: ' + (err as Error).message;
                      setEinreichErgebnis({ ok: false, text: detail });
                    } finally {
                      setEinreichBusy(false);
                    }
                  }}
                >
                  {einreichBusy ? 'Wird eingereicht…' : einreichErgebnis?.ok ? '✓ Eingereicht' : 'Verbindlich einreichen'}
                </Button>
                {einreichErgebnis && (
                  <Alert variant={einreichErgebnis.ok ? 'success' : 'danger'} className='mt-2 mb-0 py-2'>
                    {einreichErgebnis.text}
                  </Alert>
                )}
                {appMode === 'neuantrag' && einreichErgebnis?.ok && (
                  ausweisSendeErgebnis ? (
                    <Alert variant='success' className='mt-2 mb-0 py-2'>📨 {ausweisSendeErgebnis}</Alert>
                  ) : ausweisSendeFehler ? (
                    <Alert variant='danger' className='mt-2 mb-0 py-2'>
                      {ausweisSendeFehler}
                      <div className='mt-2'>
                        <Button size='sm' variant='outline-danger' disabled={ausweisSendet} onClick={handleAusweisSenden}>
                          {ausweisSendet ? 'Wird gesendet …' : 'Flughafen-Formulare erneut senden'}
                        </Button>
                      </div>
                    </Alert>
                  ) : (
                    <Alert variant='info' className='mt-2 mb-0 py-2'>📨 Deine Flughafenausweis-Formulare gehen gerade an die Ausweisstelle …</Alert>
                  )
                )}
              </div>}
              <details>
                <summary className='text-muted' style={{ cursor: 'pointer', fontSize: '0.9rem' }}>Alternative: per E-Mail einreichen</summary>
                <p className='mt-2'>1. Datei generieren: <a href={jsonUrl} download={sanitizeFilename('zuep_' + new Date().toISOString().split('T')[0] + '_' + (json as any)!.famname + '_' + (json as any)!.vorname) + '.zip'}>Zip Datei generieren und abspeichern</a></p>
                <p>2. An <a href={'mailto:schriftfuehrer@flugsportzentrum.at?subject=ZÜP-Antrag ' + (json as any)!.vorname + ' ' + (json as any)!.famname}>schriftfuehrer@flugsportzentrum.at</a> senden (<strong>Zip Datei bitte anhängen</strong>).</p>
              </details>
              {appMode === 'neuantrag' && (
                <>
                  <hr/>
                  <p>3. Die enthaltenen PDFs (ANTRAG & ANLAGE 4B) <strong>unterschreiben</strong> und selbst an <a href={'mailto:security@innsbruck-airport.com?subject=Flughafenausweis ' + ((json as any)?.vorname || '') + ' ' + ((json as any)?.famname || '')}>security@innsbruck-airport.com</a> senden.</p>
                </>
              )}
              <hr/>
              <p className='text-muted' style={{fontSize: '0.85rem'}}>Die ZIP-Datei ist deine persönliche Sicherung — beim nächsten Mal werden die Felder automatisch aus der ZÜP-Datenbank vorbefüllt.</p>
            </>
          )}
        </Modal.Body>
      </Modal>}
    </div >
  );
}

export default App;

